// ReconcilePage — Phase 2B, financial-only, DESKTOP-first (Dawn reconciles on a
// desktop; only the scanners are on phones). The board segments a billing period
// of one card account into six collapsible piles, each grouped by cardholder, so attention goes only
// where it's needed:
//   Confident  : deterministic, unambiguous 1:1 matches, per-cardholder-section confirm
//   Needs eyes : fuzzy or ambiguous suggestions, confirm/reject one at a time; also
//                subscription hints and proposals (recurringCopy.ts)
//   No receipt : unmatched charges (who owes one)
//   Refunds    : credits, auto-resolved, take the account of the purchase they reverse
//   Recurring  : remembered subscriptions, auto-resolved, open so Dawn eyeballs them
//   Done       : confirmed + exceptions, collapsed out of the way
// The engine only ever suggests; every confirm is a human action.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { apiFetchAuthed } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import { useConfirm } from '../components/ConfirmModal/ConfirmContext';
import { formatEventDate, formatTimestamp } from '../utils/date-utils';
import {
  BillingPeriod, CardAccount, CardProvider, ImportResult, ImportSummary, PeriodStatus, Upload,
  ReconBoard, ReconTxn, EXCEPTION_TYPES, exceptionTypeLabel, RecurringKind, QboSyncResult, CategoryOption, PurchaseClassOption,
} from './types';
import { formatCardLabel } from './cardLabel';
import { instance } from '../instance';
import useNudges, { nudgeCaption, NudgeSelectedButton } from './useNudges';
import MatchPicker from './MatchPicker';
import MatchPairGrid from './MatchPairGrid';
import ReceiptPeek from './ReceiptPeek';
import RequisitionChips from './RequisitionChip';
import { txnMatchesQuery } from './reconSearch';
import { refundSuggestionCaption, suggestionAdopted } from './refundSuggestion';
import { recurringSentence } from './recurringCopy';
import susStamp from '../img/sus.png';
import './susStamp.css';
import PageHeader, { SecondaryAction } from '../components/PageHeader';
import PageLayout from '../components/ui/PageLayout';
import { FOCUS } from './keyStyles';
import { buttonClass, IconButton } from '../components/ui/Button';
import { Tooltip } from '../components/ui/Tooltip';
import { Field, FieldRow } from '../components/ui/Field';
import { Checkbox } from '../components/ui/Checkbox';
import { Select } from '../components/ui/Select';
import { SearchField } from '../components/ui/SearchField';
import { RadioGroup, Radio } from '../components/ui/Radio';
import { Tag } from '../components/ui/Tag';
import { Alert } from '../components/ui/Alert';
import { EmptyState } from '../components/ui/EmptyState';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import { Panel } from '../components/ui/Panel';
import { Progress } from '../components/ui/Progress';
import Reveal from '../components/ui/Reveal';
import Appear from '../components/ui/Appear';
import FadeIn from '../components/ui/FadeIn';
import { useFilePicker } from '../components/ui/FilePick';
import { IconClose, IconPencil } from '../components/ui/icons';


// A block in a `space-y-N` column that comes and goes. The gap rides inside the Reveal as
// padding, and the wrapper's -mb cancels the column's margin on the next sibling while
// the block is mounted; `empty:` restores it once Reveal has unmounted (the column's own
// margin then supplies the gap). Without this the closing block leaves a hop behind.
const SLOT = {
  '1.5': '!-mb-1.5 empty:!mb-0 empty:!mt-0',
  '4': '!-mb-4 empty:!mb-0 empty:!mt-0',
} as const;
const SLOT_PAD = { '1.5': 'pb-1.5', '4': 'pb-4' } as const;
function Slot({ show, gap, children }: { show: boolean; gap: keyof typeof SLOT; children: React.ReactNode }) {
  return (
    <div className={SLOT[gap]}>
      <Reveal show={show}><div className={SLOT_PAD[gap]}>{children}</div></Reveal>
    </div>
  );
}

const money = (v: unknown) => (v == null || v === '' ? '—' : `$${Math.abs(Number(v)).toFixed(2)}`);
// Chase statement rows are signed: purchases negative, returns/credits positive.
// Purchases still read as a plain dollar figure; credits get an explicit "+" so
// a $50 return doesn't look like just another $50 charge.
const isCredit = (v: unknown) => Number(v) > 0;
const txnMoney = (v: unknown) => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  return n > 0 ? `+$${n.toFixed(2)}` : `$${Math.abs(n).toFixed(2)}`;
};

type Tier = 'confident' | 'review' | 'noreceipt' | 'refunds' | 'recurring' | 'done';
const tierOf = (t: ReconTxn): Tier => {
  // Credits (returns/refunds) never get matched to a receipt — the backend
  // auto-resolves them on import/rematch. They live in their own pile
  // regardless of resolution state, so this check comes before match/exception.
  if (isCredit(t.amount)) return 'refunds';
  if (t.match_status === 'matched') return 'done';
  if (t.exception?.exception_type === 'recurring') return 'recurring';
  if (t.exception) return 'done';
  if (t.match_status === 'suggested' && t.match) {
    return t.match.match_kind === 'deterministic' && !t.match.ambiguous ? 'confident' : 'review';
  }
  if ((t.recurring_hint || t.recurring_proposal) && !t.recurring_decision) return 'review';
  return 'noreceipt';
};

// Cardholder label a charge belongs to, e.g. "2919 - Kevin Bisel".
const cardOf = (t: ReconTxn) =>
  (t.card_label || t.card_last4) ? formatCardLabel({ label: t.card_label || '', last4: t.card_last4 }) : 'Unknown card';

function groupByCard(txns: ReconTxn[]): [string, ReconTxn[]][] {
  const m = new Map<string, ReconTxn[]>();
  txns.forEach((t) => {
    const key = cardOf(t);
    if (!m.has(key)) m.set(key, []);
    m.get(key)!.push(t);
  });
  return Array.from(m.entries()).sort((a, b) => b[1].length - a[1].length);
}

// ── keyboard-driven review ──────────────────────────────────────────────────
// A single focus cursor over a flattened list of ACTIONABLE rows — every txn in
// the review pile then the no-receipt pile, in rendered order (cardholder-group
// order within each tier). Confident/Done rows are NOT in the ring (bulk-confirm
// handles confident; done needs nothing). The cursor is a UI overlay: it drives a
// visible ring + auto-scroll, but never steals real DOM focus or fights the mouse.
//
// Reload-survival: focus is keyed by txn id, not index. After a board reload the
// focused row often leaves the actionable set (confirm/exception → Done) — we then
// advance to whatever is now at the same slot (the list shrank), which reads as
// "next item". Reject is the one action that keeps the row actionable (it drops to
// no-receipt), so it carries an explicit advance-by-index intent to skip past it.
//
// Search interaction: the cursor walks the FILTERED rows, so j/k reviews exactly
// what the query surfaced. While the query string is changing the cursor is
// dropped rather than re-homed — otherwise every keystroke would re-point it and
// the auto-scroll below would yank the page out from under her typing.
function useKeyboardReview(opts: {
  groups: Record<Tier, ReconTxn[]>;
  selectedId: number | null;
  busy: boolean;
  peekOpen: boolean;
  pickerOpen: boolean;
  exceptionOpen: boolean;
  query: string;
  clearQuery: () => void;
  searchRef: React.RefObject<HTMLInputElement | null>;
  setOpen: React.Dispatch<React.SetStateAction<Record<Tier, boolean>>>;
  setOpenGroups: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  confirmMatch: (matchId: number) => void;
  rejectMatch: (matchId: number) => void;
  openPicker: (txnId: number) => void;
  openException: (txnId: number) => void;
  openPeek: (pid: string) => void;
  closePeek: () => void;
  closePicker: () => void;
  closeException: () => void;
}) {
  const { groups, selectedId, query, setOpen, setOpenGroups } = opts;
  const [focusedId, setFocusedId] = useState<number | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);

  // Flattened actionable rows, in the exact order GroupedBody renders them.
  const flatten = (txns: ReconTxn[]) => groupByCard(txns).flatMap(([, rows]) => rows);
  const actionable = useMemo(
    () => [...flatten(groups.review), ...flatten(groups.noreceipt)],
    [groups], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const prevIdxRef = useRef(-1);   // last valid focused index, survives a reload
  const advanceRef = useRef<number | null>(null); // set by confirm/reject: advance here after reload

  const curIdx = focusedId == null ? -1 : actionable.findIndex((t) => t.id === focusedId);

  // Track the last known-good index so a reload that drops the focused row can land
  // on its neighbor. -1 (row gone) intentionally does NOT overwrite it.
  useEffect(() => { if (curIdx >= 0) prevIdxRef.current = curIdx; }, [curIdx]);

  const lastQueryRef = useRef(query.trim());

  // Reconcile focus after any board reload (actionable identity changed).
  // A query edit takes priority: it churns `actionable` on every keystroke, and
  // re-homing there would fight the typist, so the cursor is simply dropped and
  // this pass skips. The next pass sees a settled query and falls through.
  useEffect(() => {
    const len = actionable.length;
    // Compare trimmed: a trailing space changes the string but not the result set,
    // and dropping the cursor for it would be gratuitous.
    if (lastQueryRef.current !== query.trim()) {
      lastQueryRef.current = query.trim();
      prevIdxRef.current = -1;
      advanceRef.current = null;
      if (focusedId != null) setFocusedId(null);
      return;
    }
    if (advanceRef.current != null) {
      const target = advanceRef.current;
      advanceRef.current = null;
      setFocusedId(len === 0 ? null : actionable[Math.min(target, len - 1)].id);
      return;
    }
    if (focusedId == null) return;
    if (actionable.some((t) => t.id === focusedId)) return; // still present, keep it
    setFocusedId(len === 0 ? null : actionable[Math.min(prevIdxRef.current, len - 1)].id);
  }, [actionable, focusedId, query]);

  // New period → drop focus entirely.
  useEffect(() => {
    setFocusedId(null);
    setHelpOpen(false);
    prevIdxRef.current = -1;
    advanceRef.current = null;
  }, [selectedId]);

  // Auto-expand the focused row's section + cardholder group, then scroll it into
  // view. Double-rAF waits one paint so a just-expanded (previously unmounted) row
  // is in the DOM before we scroll. Keyed on focusedId, so it only fires on cursor
  // moves — never on an unrelated section toggle (that would fight the mouse).
  useEffect(() => {
    if (focusedId == null) return;
    const txn = actionable.find((t) => t.id === focusedId);
    if (!txn) return;
    const tier = tierOf(txn);
    const key = `${tier}::${cardOf(txn)}`;
    setOpen((o) => (o[tier] ? o : { ...o, [tier]: true }));
    setOpenGroups((o) => (o[key] ? o : { ...o, [key]: true }));
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        document.querySelector(`[data-rowid="${focusedId}"]`)?.scrollIntoView({ block: 'nearest' });
      });
    });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, [focusedId, actionable, setOpen, setOpenGroups]);

  // Live snapshot for the single, once-attached keydown listener.
  const live = useRef(opts);
  live.current = opts;
  const actionableRef = useRef(actionable);
  actionableRef.current = actionable;
  const focusedIdRef = useRef(focusedId);
  focusedIdRef.current = focusedId;
  const helpRef = useRef(helpOpen);
  helpRef.current = helpOpen;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // The useConfirm modal (aria-modal) owns the keyboard while it's up — this is
      // the one React modal that renders over the board (import uses the OS file
      // dialog, ReceiptPeek is role=dialog but NOT aria-modal).
      if (document.querySelector('[aria-modal="true"]')) return;

      const s = live.current;
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;

      // Escape is allowed even from a field (so the exception <select> can close and
      // hand focus back to the ring). Ordered close: search, help, peek, picker,
      // exception, else clear the cursor.
      if (e.key === 'Escape') {
        // From inside the search box Escape is about the search: first press wipes
        // the query, second hands the keyboard back to the board.
        if (el && el === s.searchRef.current) {
          if (s.query) { s.clearQuery(); return; }
          (el as HTMLInputElement).blur();
          return;
        }
        if (helpRef.current) { setHelpOpen(false); return; }
        if (s.peekOpen) { s.closePeek(); return; }
        if (s.pickerOpen) { s.closePicker(); return; }
        if (s.exceptionOpen) { s.closeException(); return; }
        if (focusedIdRef.current != null) setFocusedId(null);
        return;
      }

      const typing = tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || !!el?.isContentEditable;
      if (typing) return;

      // Sits after the typing guard, so a slash typed INTO the box is just a slash.
      if (e.key === '/') { e.preventDefault(); s.searchRef.current?.focus(); return; }

      if (e.key === '?') { setHelpOpen((v) => !v); return; }

      // Let a focused button/link keep its native Enter/Space activation.
      if ((e.key === 'Enter' || e.key === ' ') && (tag === 'BUTTON' || tag === 'A')) return;

      const list = actionableRef.current;
      const idx = focusedIdRef.current == null ? -1 : list.findIndex((t) => t.id === focusedIdRef.current);
      const txn: ReconTxn | null = idx >= 0 ? list[idx] : null;
      const overlay = s.peekOpen || s.pickerOpen || s.exceptionOpen;

      switch (e.key) {
        case 'j': case 'ArrowDown': {
          if (overlay || list.length === 0) return; // Escape first while an overlay is open
          e.preventDefault();
          advanceRef.current = null;
          setFocusedId(list[idx < 0 ? 0 : Math.min(idx + 1, list.length - 1)].id);
          return;
        }
        case 'k': case 'ArrowUp': {
          if (overlay || list.length === 0) return;
          e.preventDefault();
          advanceRef.current = null;
          setFocusedId(list[idx < 0 ? 0 : Math.max(idx - 1, 0)].id);
          return;
        }
        case 'Enter': case 'y': {
          if (!txn || s.busy || tierOf(txn) !== 'review' || !txn.match) return;
          advanceRef.current = idx;
          s.confirmMatch(txn.match.id);
          return;
        }
        case 'r': {
          if (!txn || s.busy || tierOf(txn) !== 'review' || !txn.match) return;
          advanceRef.current = idx;
          s.rejectMatch(txn.match.id);
          return;
        }
        case 'e': {
          if (!txn || tierOf(txn) !== 'noreceipt') return; // exception select only exists on no-receipt rows
          s.openException(txn.id);
          return;
        }
        case 'f': {
          if (!txn) return;
          s.openPicker(txn.id);
          return;
        }
        case 'p': {
          if (!txn || tierOf(txn) !== 'review' || !txn.match) return;
          s.openPeek(txn.match.receipt_public_id);
          return;
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []); // attach once; reads live refs

  return { focusedId, helpOpen, setHelpOpen };
}

// ── the library: the /reconcile landing ──────────────────────────────────────
// You open Reconcile and see the accounts, each with its billing periods newest
// first; pick a period and you're working inside it (/reconcile/periods/:periodId).
// Periods are cut from each account's closing day, never made by hand. Import,
// the closing day and the uploads record live here, on the account they belong to.
type UnmanagedCard = { last4: string; account_id: number | null; txn_count: number; last_seen: string | null };
// A refusal about the import key that was pressed ('any' = the header's).
type ImportError = { target: number | 'any'; message: string };

const importToast = (res: ImportResult) => {
  if (res.duplicate) return 'That statement was already imported';
  const n = res.imported;
  const already = res.summary?.already_on_file || 0;
  const refused = res.summary?.into_finalized?.count || 0;
  return `Imported ${n} charge${n === 1 ? '' : 's'}${already > 0 ? `, ${already} already on file` : ''}`
    + (refused > 0 ? `, ${refused} fell inside a finalized period and ${refused === 1 ? 'was' : 'were'} not taken` : '');
};

// The message the server put on a refusal (409 overlap, label taken, bad dates).
const serverMessage = (e: any, fallback: string) => e?.data?.error?.message || e?.message || fallback;

export default function ReconcileManager() {
  const toast = useToast();
  const history = useHistory();
  const [accounts, setAccounts] = useState<CardAccount[]>([]);
  const [loading, setLoading] = useState(true);
  // 'any' = the header's import, which lets the server work out the account.
  const [importingFor, setImportingFor] = useState<number | 'any' | null>(null);
  const importTarget = useRef<number | null>(null);
  const [importError, setImportError] = useState<ImportError | null>(null);
  // A file the server could not place: which account is it from?
  const [pickFor, setPickFor] = useState<{ file: File; last4s: string[]; format: CardProvider | null } | null>(null);
  const [addingAccount, setAddingAccount] = useState(false);
  // Bumped after an import or a delete so an open uploads list refetches.
  const [uploadsKey, setUploadsKey] = useState(0);
  // Card last4s on imported charges with no card record, one row per account.
  // (Auto-confirm exists in the engine but is deliberately UI-less: the Confident
  // pile's per-cardholder Confirm is the one confirmation workflow.)
  const [unmanaged, setUnmanaged] = useState<UnmanagedCard[]>([]);

  const loadAccounts = useCallback(async () => {
    setAccounts((await apiFetchAuthed('/reconciliation/accounts')) || []);
  }, []);
  const loadUnmanaged = useCallback(() => {
    apiFetchAuthed('/cards/unmanaged', { silent: true })
      .then((u: any) => setUnmanaged(u || []))
      .catch(() => { /* leave empty */ });
  }, []);

  useEffect(() => {
    setLoading(true);
    loadAccounts()
      .catch((e: any) => toast.error(e?.message || 'Could not load accounts'))
      .finally(() => setLoading(false));
  }, [loadAccounts, toast]);
  useEffect(() => { loadUnmanaged(); }, [loadUnmanaged]);

  const refresh = async () => {
    try { await loadAccounts(); } catch (e: any) { toast.error(e?.message || 'Could not load accounts'); }
    loadUnmanaged();
    setUploadsKey((k) => k + 1);
  };

  const picker = useFilePicker({
    accept: '.csv',
    inputProps: { 'data-testid': 'statement-file' },
    onFiles: (files) => { runImport(files[0], importTarget.current); },
  });

  const startImport = (accountId: number | null) => {
    importTarget.current = accountId;
    picker.open();
  };

  const runImport = async (file: File, accountId: number | null) => {
    setImportingFor(accountId ?? 'any');
    setImportError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (accountId != null) fd.append('account_id', String(accountId));
      const res: ImportResult = await apiFetchAuthed('/reconciliation/import', { method: 'POST', body: fd, silent: true });
      setPickFor(null);
      toast.success(importToast(res));
      if (res.period_ids.length) {
        // Import continues INTO the board (a flow, not a landing), the newest
        // period: the one still filling. period_ids come oldest first. No state
        // writes after this: the library unmounts.
        history.push(`/reconcile/periods/${res.period_ids[res.period_ids.length - 1]}`);
        return;
      }
      setImportingFor(null);
      await refresh();
    } catch (e: any) {
      setImportingFor(null);
      const err = e?.data?.error;
      if (e?.status === 409 && err?.code === 'account_required') {
        setPickFor({
          file,
          last4s: Array.isArray(err.last4s) ? err.last4s : [],
          format: err.format === 'chase' || err.format === 'amex' ? err.format : null,
        });
        return;
      }
      if (e?.status === 409 && err?.code === 'closing_day_required') {
        setImportError({ target: accountId ?? 'any', message: serverMessage(e, 'Set the closing day first') });
        return;
      }
      toast.error(serverMessage(e, 'Import failed'));
    }
  };

  const importInput = picker.input;


  return (
    <PageLayout width="full" kind="workspace" className="pt-4 lg:pt-6" header={(
        <PageHeader
          className="mb-6"
          back={{ to: "/receipts", label: "Receipts" }}
          title="Reconcile"
          actions={<>
            <SecondaryAction variant="text" to="/receipts/ledger">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" /></svg>
              Ledger
            </SecondaryAction>
            <SecondaryAction variant="text" to="/receipts/insights">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M3 3v18h18M7 15l3.5-4.5 3 3L18 8" /></svg>
              Insights
            </SecondaryAction>
            <SecondaryAction variant="text" to="/admin/cards">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9v9.75c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125V9m-19.5 0V6.75c0-.621.504-1.125 1.125-1.125h17.25c.621 0 1.125.504 1.125 1.125V9" /></svg>
              Manage cards
            </SecondaryAction>
          </>}
        />
    )}>
      {importInput}
      {/* The financial workspace's sibling surfaces live here, not on /receipts. */}

      <Reveal show={!!pickFor}>
        {pickFor && (
          <AccountPicker accounts={accounts} last4s={pickFor.last4s} format={pickFor.format} busy={importingFor != null}
            onPick={(id) => runImport(pickFor.file, id)}
            onCreated={async (acc) => { await loadAccounts().catch(() => {}); runImport(pickFor.file, acc.id); }}
            onCancel={() => setPickFor(null)} />
        )}
      </Reveal>

      <FadeIn key={loading ? 'loading' : accounts.length === 0 ? 'none' : 'accounts'}>
      {loading ? (
        <SkeletonGroup label="Loading accounts" className="space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-40 rounded-panel" />)}</SkeletonGroup>
      ) : accounts.length === 0 ? (
        <EmptyState
          title="No accounts yet"
          action={(
            <div className="w-full max-w-xl text-left">
              <AddAccountForm onCreated={() => refresh()} />
            </div>
          )}
        >
          Add the account the statement belongs to, then import its file.
        </EmptyState>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-on-surface-variant">{accounts.length} account{accounts.length === 1 ? '' : 's'}</p>
            <div className="flex items-center gap-3">
              <Tooltip label="Add another card account, such as a second Chase or an Amex">
                <button type="button" onClick={() => setAddingAccount((v) => !v)} aria-expanded={addingAccount}
                  className={buttonClass('tonal', 'md')}>
                  Add account
                </button>
              </Tooltip>
              <Tooltip label="Pick a statement file. The app files it under the account its cards belong to">
                <button type="button" onClick={() => startImport(null)} disabled={importingFor != null}
                  className={buttonClass('filled', 'md')}>
                  {importingFor === 'any' ? 'Importing…' : 'Import statement'}
                </button>
              </Tooltip>
            </div>
          </div>
          <Slot gap="4" show={importError?.target === 'any'}>
            <Alert variant="text" className="justify-end text-right text-xs">{importError?.message}</Alert>
          </Slot>
          <Slot gap="4" show={addingAccount}>
            <AddAccountForm onCreated={async () => { setAddingAccount(false); await refresh(); }} onCancel={() => setAddingAccount(false)} />
          </Slot>
          {accounts.map((a) => (
            <AccountCard key={a.id} account={a}
              unmanaged={unmanaged.filter((u) => String(u.account_id) === String(a.id))}
              importing={importingFor === a.id}
              importDisabled={importingFor != null}
              onImport={() => startImport(a.id)}
              onChanged={refresh}
              uploadsKey={uploadsKey}
              importError={importError?.target === a.id ? importError.message : null} />
          ))}
        </div>
      )}
      </FadeIn>
    </PageLayout>
  );
}

// Period status in Dawn's words. Color reinforces the word, never replaces it.
// An open period is one still being worked, so it alone carries the accent.
const PERIOD_CHIP: Record<PeriodStatus, { label: string; tone: 'accent' | 'good' }> = {
  open:       { label: 'In progress',   tone: 'accent' },
  reconciled: { label: 'Finalized',     tone: 'good' },
  exported:   { label: 'Exported',      tone: 'good' },
  synced:     { label: 'Synced to QBO', tone: 'good' },
};

// The cycle that contains today. Quiet on purpose: it is a fact about the
// calendar, not a state that needs her.
function CurrentChip() {
  return <Tag>Current</Tag>;
}

function PeriodChip({ status }: { status: PeriodStatus }) {
  const c = PERIOD_CHIP[status] || PERIOD_CHIP.open;
  return <Tag tone={c.tone}>{c.label}</Tag>;
}

const PROVIDER_LABEL: Record<CardProvider, string> = { chase: 'Chase', amex: 'American Express' };

// 1 → "1st", 22 → "22nd", 13 → "13th".
export function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] || 'th'}`;
}
// A closing day as typed: a whole number 1..31, else null.
const parseClosingDay = (v: string): number | null => {
  const n = Number(v);
  return Number.isInteger(n) && n >= 1 && n <= 31 ? n : null;
};
const CLOSING_DAY_HINT = 'The day of the month the statement closes. A 31 closes on the last day of shorter months.';

// React 17 has no useId; forms that can appear twice on a page need distinct ids.
let formSeq = 0;
const useFormId = (prefix: string) => useMemo(() => `${prefix}-${++formSeq}`, []); // eslint-disable-line react-hooks/exhaustive-deps


// Adding an account: a name she knows it by, which bank's file it takes, and the
// day its statement closes (the periods are cut from it).
// Used on the empty library, from "Add account", and inside the account picker
// so an unplaced file never sends her elsewhere to make one.
function AddAccountForm({ onCreated, onCancel, initialProvider = 'chase' }: {
  onCreated: (a: CardAccount) => void; onCancel?: () => void; initialProvider?: CardProvider;
}) {
  const [label, setLabel] = useState('');
  const [provider, setProvider] = useState<CardProvider>(initialProvider);
  const [closingDay, setClosingDay] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameId = useFormId('account-name');
  const dayId = useFormId('closing-day');

  const submit = async () => {
    const name = label.trim();
    if (!name) { setError('Give the account a name'); return; }
    const day = parseClosingDay(closingDay);
    if (day == null) { setError('Enter the closing day, a number from 1 to 31'); return; }
    setSaving(true);
    setError(null);
    try {
      const a: CardAccount = await apiFetchAuthed('/reconciliation/accounts', {
        method: 'POST', body: { label: name, provider, closing_day: day }, silent: true,
      });
      setSaving(false);
      setLabel('');
      setClosingDay('');
      onCreated(a);
    } catch (e: any) {
      setError(serverMessage(e, 'Could not add the account'));
      setSaving(false);
    }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
     <Panel label="Add an account" on="panel" className="gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <FieldRow label="Account name" htmlFor={nameId} className="flex-1 min-w-[12rem]">
          <Field id={nameId} autoFocus type="text" value={label} onChange={(e) => setLabel(e.target.value)} disabled={saving} maxLength={128}
            placeholder="What you call it, e.g. Chase Ink" />
        </FieldRow>
        <RadioGroup label="Bank" layout="row" value={provider} onChange={(v) => setProvider(v as CardProvider)} disabled={saving}>
          {(Object.keys(PROVIDER_LABEL) as CardProvider[]).map((p) => (
            <Radio key={p} value={p} label={PROVIDER_LABEL[p]} />
          ))}
        </RadioGroup>
        <FieldRow label="Closing day" htmlFor={dayId} className="w-28">
          <Tooltip label={CLOSING_DAY_HINT}>
            <Field id={dayId} type="number" min={1} max={31} step={1} inputMode="numeric" value={closingDay} required
              onChange={(e) => setClosingDay(e.target.value)} disabled={saving} placeholder="17" />
          </Tooltip>
        </FieldRow>
        <button type="submit" disabled={saving}
          className={buttonClass('filled', 'md')}>
          {saving ? 'Adding…' : 'Add account'}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} disabled={saving} className={buttonClass('quiet', 'md')}>Cancel</button>
        )}
      </div>
      <Reveal show={!!error}><Alert variant="text" className="text-xs">{error}</Alert></Reveal>
     </Panel>
    </form>
  );
}

// The server could not tell which account a file belongs to (no account holds
// its cards, or two hold them equally). She says which, or adds the account here.
function AccountPicker({ accounts: all, last4s, format, busy, onPick, onCreated, onCancel }: {
  accounts: CardAccount[]; last4s: string[]; format: CardProvider | null; busy: boolean;
  onPick: (id: number) => void; onCreated: (a: CardAccount) => void; onCancel: () => void;
}) {
  // A file only goes into an account of its own bank; the server refuses the rest.
  const accounts = format ? all.filter((a) => a.provider === format) : all;
  const [adding, setAdding] = useState(accounts.length === 0);
  return (
    <Panel label="Pick the account" className="mb-5 gap-3">
      <div>
        <p className="text-sm font-semibold text-on-surface">
          Which {format ? `${PROVIDER_LABEL[format]} ` : ''}account is this statement from?
        </p>
        <p className="text-xs text-on-surface-variant mt-0.5">
          {last4s.length
            ? <>The file has cards ending <span className="font-mono tabular-nums text-on-surface">{last4s.join(', ')}</span>, and no single account holds them.</>
            : 'No account holds the cards on this file.'}
        </p>
      </div>
      {accounts.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {accounts.map((a) => (
            <Tooltip key={a.id} label={a.closing_day == null ? 'Set this account\'s closing day first, on its card below' : `Import into ${a.label}`}>
              <button type="button" onClick={() => onPick(a.id)} disabled={busy || a.closing_day == null}
                className={`${buttonClass('tonal', 'md')} text-left`}>
                <span className="text-sm text-on-surface">{a.label}</span>
                <span className="text-xs text-on-surface-variant">{PROVIDER_LABEL[a.provider]}</span>
              </button>
            </Tooltip>
          ))}
        </div>
      )}
      <FadeIn key={adding ? 'form' : 'button'}>
      {adding ? (
        <AddAccountForm onCreated={onCreated} initialProvider={format || undefined}
          onCancel={accounts.length ? () => setAdding(false) : undefined} />
      ) : (
        <button type="button" onClick={() => setAdding(true)} className={buttonClass('quiet', 'sm')}>
          It is a new account
        </button>
      )}
      </FadeIn>
      <div>
        <button type="button" onClick={onCancel} disabled={busy} className={buttonClass('quiet', 'md')}>
          Cancel the import
        </button>
      </div>
    </Panel>
  );
}

// An account's name in its card header, with an inline rename.
function AccountName({ account, onRenamed }: { account: CardAccount; onRenamed: () => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    const label = draft.trim();
    if (!label) { setError('Give the account a name'); return; }
    if (label === account.label) { setEditing(false); return; }
    setSaving(true);
    try {
      await apiFetchAuthed(`/reconciliation/accounts/${account.id}`, { method: 'PATCH', body: { label }, silent: true });
      setEditing(false);
      setError(null);
      onRenamed();
    } catch (e: any) {
      setError(serverMessage(e, 'Could not rename the account'));
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <form className="flex flex-wrap items-center gap-2 min-w-0" onSubmit={(e) => { e.preventDefault(); save(); }}>
        <Field autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={128} disabled={saving}
          onKeyDown={(e) => { if (e.key === 'Escape') { setEditing(false); setError(null); } }}
          aria-label="Account name" className="!w-auto min-w-[12rem]" />
        <button type="submit" disabled={saving} className={buttonClass('filled', 'sm')}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={() => { setEditing(false); setError(null); }} disabled={saving}
          className={buttonClass('quiet', 'sm')}>Cancel</button>
        <div className="basis-full"><Reveal show={!!error}><Alert variant="text" className="text-xs">{error}</Alert></Reveal></div>
      </form>
    );
  }
  return (
    <div className="flex items-center gap-2 min-w-0">
      <h2 className="text-base font-semibold text-on-surface truncate">{account.label}</h2>
      <span className="text-xs text-on-surface-variant whitespace-nowrap">{PROVIDER_LABEL[account.provider] || account.provider}</span>
      <Tooltip label="Rename account">
        <IconButton size="sm" label="Rename account" onClick={() => { setDraft(account.label); setEditing(true); }}>
          <IconPencil />
        </IconButton>
      </Tooltip>
    </div>
  );
}

function AccountCard({
  account, unmanaged, importing, importDisabled, onImport, onChanged, uploadsKey, importError,
}: {
  account: CardAccount;
  unmanaged: UnmanagedCard[];
  importing: boolean;
  importDisabled: boolean;
  onImport: () => void;
  onChanged: () => Promise<void> | void;
  uploadsKey: number;
  importError: string | null;
}) {
  const periods = account.periods || [];
  const outside = account.outside_count || 0;
  const noClosingDay = account.closing_day == null;

  return (
    <Panel label={account.label} padding="none" className="overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
        <div className="min-w-0 space-y-0.5">
          <AccountName account={account} onRenamed={() => onChanged()} />
          {!noClosingDay && <ClosingDayLine account={account} onSaved={onChanged} />}
          <Reveal show={outside > 0}>
            <p className="text-[11px] text-amber-300/90">
              {outside} charge{outside === 1 ? ' falls' : 's fall'} outside every period
            </p>
          </Reveal>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Tooltip label={noClosingDay
            ? 'Set the closing day first. The periods are cut from it'
            : `Pick a ${PROVIDER_LABEL[account.provider] || ''} statement file to add to ${account.label}`}>
            <button type="button" onClick={onImport} disabled={importDisabled || noClosingDay}
              className={buttonClass('quiet', 'md')}>
              {importing ? 'Importing…' : 'Import statement'}
            </button>
          </Tooltip>
          <Reveal show={!!importError}><Alert variant="text" className="max-w-xs justify-end text-right text-xs">{importError}</Alert></Reveal>
        </div>
      </div>

      <Reveal show={noClosingDay}>
        <div className="mx-4 pb-3">
        <Alert tone="warning" title="Set the closing day to start importing">
          <p className="m-0 max-w-[62ch]">{CLOSING_DAY_HINT} Each period ends on it and the next starts the day after.</p>
          <div className="mt-2"><ClosingDayEditor account={account} onSaved={onChanged} /></div>
        </Alert>
        </div>
      </Reveal>

      <Reveal show={unmanaged.length > 0}>
        <div className="mx-4 pb-3">
        <Alert
          tone="warning"
          title={`${unmanaged.length} card${unmanaged.length === 1 ? '' : 's'} on this account ${unmanaged.length === 1 ? 'is' : 'are'} not set up`}
          action={(
            <Link to="/admin/cards" className={buttonClass('tonal', 'md')}>
              Assign cards
            </Link>
          )}
        >
          (<span className="font-mono tabular-nums">{unmanaged.slice(0, 5).map((u) => `•• ${u.last4}`).join(', ')}{unmanaged.length > 5 ? '…' : ''}</span>).
          {' '}Charges on {unmanaged.length === 1 ? 'it' : 'them'} can't be tied to a person.
        </Alert>
        </div>
      </Reveal>

      {periods.length === 0 ? (
        <div className="border-t border-outline-variant px-4 py-5">
          <EmptyState variant="line">No periods yet. Import a statement and its charges land in periods cut from the closing day.</EmptyState>
        </div>
      ) : (
        <div className="border-t border-outline-variant divide-y divide-outline-variant">
          {periods.map((p) => <PeriodRow key={p.id} period={p} />)}
        </div>
      )}

      <UploadsHistory account={account} reloadKey={uploadsKey} onChanged={onChanged} />
    </Panel>
  );
}

// "Closes on the 17th", with a pencil that swaps in the editor.
function ClosingDayLine({ account, onSaved }: { account: CardAccount; onSaved: () => Promise<void> | void }) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <div className="pt-1 space-y-1">
        <ClosingDayEditor account={account} onSaved={async () => { setEditing(false); await onSaved(); }} onCancel={() => setEditing(false)} />
        <p className="text-[11px] text-on-surface-variant max-w-[52ch]">
          A new closing day re-cuts the periods that are not finalized. Finalized ones keep their dates.
        </p>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5 text-xs text-on-surface-variant">
      <span>Closes on the {ordinal(account.closing_day as number)}</span>
      <Tooltip label="Change the day this account's statement closes">
        <IconButton size="sm" label="Change closing day" onClick={() => setEditing(true)}>
          <IconPencil />
        </IconButton>
      </Tooltip>
    </div>
  );
}

// Sets an account's closing day. The server re-cuts the open periods from it.
function ClosingDayEditor({ account, onSaved, onCancel }: {
  account: CardAccount; onSaved: () => Promise<void> | void; onCancel?: () => void;
}) {
  const [draft, setDraft] = useState(account.closing_day != null ? String(account.closing_day) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    const day = parseClosingDay(draft);
    if (day == null) { setError('Enter a number from 1 to 31'); return; }
    if (day === account.closing_day) { onCancel?.(); return; }
    setSaving(true);
    setError(null);
    try {
      await apiFetchAuthed(`/reconciliation/accounts/${account.id}`, { method: 'PATCH', body: { closing_day: day }, silent: true });
      setSaving(false);
      await onSaved();
    } catch (e: any) {
      setError(serverMessage(e, 'Could not save the closing day'));
      setSaving(false);
    }
  };

  return (
    <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); save(); }}
      onKeyDown={(e) => { if (e.key === 'Escape' && onCancel) onCancel(); }}>
      <label className="flex items-center gap-2 text-xs text-on-surface-variant">
        Closing day
        <Field autoFocus={!!onCancel} type="number" min={1} max={31} step={1} inputMode="numeric" value={draft}
          onChange={(e) => setDraft(e.target.value)} disabled={saving} placeholder="17"
          size="sm" className="!w-20" />
      </label>
      <Tooltip label="Save the closing day and cut the periods from it">
        <button type="submit" disabled={saving}
          className={buttonClass('filled', 'sm')}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </Tooltip>
      {onCancel && (
        <button type="button" onClick={onCancel} disabled={saving} className={buttonClass('quiet', 'md')}>Cancel</button>
      )}
      {error && <Alert variant="text" className="basis-full text-xs">{error}</Alert>}
    </form>
  );
}

// One period in the library: name and status, how many charges, how far along.
// The current cycle is still filling, so it reads "Current" and counts so far.
function PeriodRow({ period: p }: { period: BillingPeriod }) {
  const t = p.tally;
  const total = t?.total ?? 0;
  const resolved = t ? t.matched + t.exceptions : 0;
  const complete = total > 0 && resolved === total;
  return (
    <Link to={`/reconcile/periods/${p.id}`}
      className="np-state grid grid-cols-[minmax(0,1fr)_7rem_11rem] items-center gap-4 px-4 py-3 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/60">
      <div className="min-w-0 flex items-center gap-2">
        <span className="text-sm font-medium text-on-surface truncate">{p.display_label}</span>
        {p.is_current ? <CurrentChip /> : <PeriodChip status={p.status} />}
      </div>
      <div className="text-xs text-on-surface-variant tabular-nums">
        {total} charge{total === 1 ? '' : 's'}{p.is_current ? ' so far' : ''}
      </div>
      <div>
        {total > 0 ? (
          <>
            <div className={`text-[11px] tabular-nums ${complete ? 'text-green-300' : 'text-on-surface-variant'}`}>{resolved} of {total} cleared</div>
            <Progress className="mt-1" size="sm" label={`${p.display_label} progress`} value={resolved} max={total} success={complete} />
          </>
        ) : (
          <span className="text-[11px] text-on-surface-variant">No charges yet</span>
        )}
      </div>
    </Link>
  );
}

// The account's uploads: which files came in, when, from whom. A record, not a
// work surface. Only the newest can be deleted (older ones are what later files
// were checked against), and the server says why when it refuses.
function UploadsHistory({ account, reloadKey, onChanged }: { account: CardAccount; reloadKey: number; onChanged: () => Promise<void> | void }) {
  const toast = useToast();
  const confirmDialog = useConfirm();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<Upload[] | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const count = account.uploads_count ?? rows?.length ?? 0;

  const load = useCallback(() => {
    apiFetchAuthed(`/reconciliation/accounts/${account.id}/uploads`, { silent: true })
      .then((r: Upload[]) => setRows(r || []))
      .catch((e: any) => { setRows([]); toast.error(e?.message || 'Could not load the uploads'); });
  }, [account.id, toast]);

  useEffect(() => { if (open) load(); }, [open, reloadKey, load]);

  const remove = async (u: Upload) => {
    const name = u.source_filename || u.label || 'this statement';
    const n = u.rows_held ?? 0;
    const ok = await confirmDialog({
      title: `Delete ${name}?`,
      message: `This removes the statement and the ${n} charge${n === 1 ? '' : 's'} it brought in. Charges an earlier statement also had stay. Confirmed matches must be undone first.`,
      confirmLabel: 'Delete statement',
      tone: 'danger',
    });
    if (!ok) return;
    setDeleting(u.id);
    try {
      await apiFetchAuthed(`/reconciliation/uploads/${u.id}`, { method: 'DELETE', silent: true });
      toast.success('Statement deleted');
      await onChanged();
    } catch (e: any) {
      toast.error(serverMessage(e, 'Could not delete this statement'));
    } finally {
      setDeleting(null);
    }
  };

  if (!count && !open) return null;
  return (
    <div className="border-t border-outline-variant">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className="np-state w-full flex items-center gap-2 px-4 py-2 text-left text-xs text-on-surface-variant hover:text-on-surface transition-colors motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/50">
        <Chevron open={open} />
        <span>Uploads</span>
        <span className="tabular-nums text-on-surface-variant">{count}</span>
      </button>
      <Reveal show={open}>
        <FadeIn key={rows == null ? 'loading' : rows.length === 0 ? 'none' : 'rows'}>
        {rows == null ? (
          <SkeletonGroup label="Loading uploads" className="px-4 pb-3 space-y-1.5">{[0, 1].map((i) => <Skeleton key={i} className="h-7" />)}</SkeletonGroup>
        ) : rows.length === 0 ? (
          <EmptyState variant="line" dense className="px-4 pb-3">No statements imported into this account yet.</EmptyState>
        ) : (
          <div className="pb-2">
            {rows.map((u) => (
              <div key={u.id} className="grid grid-cols-[minmax(0,1fr)_minmax(0,14rem)_6rem_4.5rem] items-center gap-3 px-4 py-1.5 text-xs">
                <span className="min-w-0 truncate text-on-surface" title={u.source_filename || u.label || ''}>{u.source_filename || u.label || 'Statement'}</span>
                <span className="min-w-0 truncate text-on-surface-variant">
                  {formatTimestamp(u.created_at)}{u.imported_by_name ? ` by ${u.imported_by_name}` : ''}
                </span>
                <span className="text-on-surface-variant tabular-nums">{u.rows_held ?? 0} charge{u.rows_held === 1 ? '' : 's'}</span>
                <span className="justify-self-end">
                  {u.is_latest && (
                    <Tooltip label="Delete this statement and the charges it brought in. Only the newest one can go">
                      <button type="button" onClick={() => remove(u)} disabled={deleting != null}
                        className={buttonClass('danger-quiet', 'sm')}>
                        {deleting === u.id ? 'Deleting…' : 'Delete'}
                      </button>
                    </Tooltip>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
        </FadeIn>
      </Reveal>
    </div>
  );
}

// ── the board: working inside one period (/reconcile/periods/:periodId) ───────
// Resolve-style: you get here by picking a period from the library. selectedId
// comes from the route param; there's no in-board switcher (you go Back to switch).
export function ReconcileBoard() {
  const toast = useToast();
  const history = useHistory();
  const confirmDialog = useConfirm();
  const { periodId } = useParams<{ periodId: string }>();
  // Import check: what each CSV contained vs what was kept, persisted on the upload
  // at import. The board payload carries only the uploads' identity, so the
  // summaries come from the account's uploads list (see the effect below).
  const [importOpen, setImportOpen] = useState(false);
  const selectedId = Number(periodId);
  const validId = Number.isInteger(selectedId) && selectedId > 0;
  const [board, setBoard] = useState<ReconBoard | null>(null);
  const [busy, setBusy] = useState(false);
  const [exceptFor, setExceptFor] = useState<number | null>(null);
  const [recurFor, setRecurFor] = useState<number | null>(null);
  // Which charge has its "find its receipt" picker expanded, and which receipt
  // (if any) is showing in the right-hand peek slide-over.
  const [pickerFor, setPickerFor] = useState<number | null>(null);
  const [peekPid, setPeekPid] = useState<string | null>(null);
  const [qboResult, setQboResult] = useState<QboSyncResult | null>(null);
  // Find one charge in a long period by any fact she remembers about it: a
  // price, a vendor, a person. Purely a lens over the piles — see `visible` below
  // for why it must not reach the rail.
  const [query, setQuery] = useState('');
  const filtering = query.trim().length > 0;
  const searchRef = useRef<HTMLInputElement | null>(null);
  // Expense categories (alphabetical) + purchase classes for Dawn's inline overrides
  // (form-options is user-session, not financial-gated — she's a user).
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [classes, setClasses] = useState<PurchaseClassOption[]>([]);
  useEffect(() => {
    apiFetchAuthed('/receipts/form-options', { silent: true })
      .then((o: any) => {
        setCategories([...(o?.categories || [])].sort((a: CategoryOption, b: CategoryOption) => a.label.localeCompare(b.label)));
        setClasses(o?.classes || []);
      })
      .catch(() => {});
  }, []);
  // Section-level collapse: only the pile that needs attention is open by default.
  const [open, setOpen] = useState<Record<Tier, boolean>>({ confident: false, review: true, noreceipt: false, refunds: false, recurring: true, done: false });
  const toggle = (k: Tier) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  // Per-cardholder collapse, keyed "<tier>::<cardholder>" so the same person in two
  // sections toggles independently. Presence = open; groups are COLLAPSED by default.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const toggleCard = (k: string) => setOpenGroups((o) => ({ ...o, [k]: !o[k] }));

  // Nudge is a check, then one button. Period-scoped, so the message can list
  // charges. The page is financial-gated already.
  const { recent: nudgeRecent, sending: nudgeSending, progress: nudgeProgress, sendSelected } = useNudges();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const togglePick = (userId: string) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(userId)) next.delete(userId); else next.add(userId);
    return next;
  });

  // An unknown/deleted period (404) or a junk param bounces back to the library.
  // fresh=true (navigation/mount loads only) asks the server to re-run matching
  // first, so receipts submitted since the last visit surface as suggestions.
  // Action refreshes pass fresh=false: pure reads, so the board never reshuffles
  // under the keyboard mid-review.
  const loadBoard = useCallback(async (id: number, fresh = false) => {
    setBusy(true);
    try { setBoard(await apiFetchAuthed(`/reconciliation/periods/${id}${fresh ? '?rematch=1' : ''}`)); }
    catch (e: any) { setBoard(null); toast.error(e?.message || 'That period could not be found'); history.replace('/reconcile'); }
    finally { setBusy(false); }
  }, [toast, history]);

  useEffect(() => {
    if (!validId) { toast.error('That period could not be found'); history.replace('/reconcile'); return; }
    loadBoard(selectedId, true); // navigation load: pick up receipts that arrived since last visit
  }, [selectedId, validId, loadBoard, history, toast]);
  // A dry-run/sync result belongs to the period it was run against: clear it
  // the moment the route moves to another period.
  useEffect(() => { setQboResult(null); setPickerFor(null); setPeekPid(null); setQuery(''); setLifecycleError(null); }, [selectedId]);

  // The import checks for the uploads that brought this period's charges in.
  const importChecks = useMemo(() => (board?.uploads || []).filter((u) => u.import_summary), [board]);
  // A finalize/reopen refusal, shown by the key that caused it.
  const [lifecycleError, setLifecycleError] = useState<string | null>(null);

  // The picker and the exception editor render INSIDE their charge's row, so a
  // query that filters that row out unmounts the UI while the flag stays set. The
  // keyboard hook reads those flags as "an overlay is open" and stops j/k, which
  // would strand her with a dead keyboard and nothing on screen to explain it.
  useEffect(() => { setPickerFor(null); setExceptFor(null); setRecurFor(null); }, [query]);

  const act = async (fn: () => Promise<any>, okMsg: string) => {
    setBusy(true);
    try { await fn(); if (selectedId) await loadBoard(selectedId); toast.success(okMsg); }
    catch (e: any) { toast.error(e?.message || 'Action failed'); }
    finally { setBusy(false); }
  };

  const confirmOne = (matchId: number) => act(() => apiFetchAuthed(`/reconciliation/matches/${matchId}/confirm`, { method: 'POST' }), 'Match confirmed, receipt approved');
  const rejectOne = (matchId: number) => act(() => apiFetchAuthed(`/reconciliation/matches/${matchId}/reject`, { method: 'POST' }), 'Suggestion dismissed');
  const unconfirmOne = (matchId: number) => act(() => apiFetchAuthed(`/reconciliation/matches/${matchId}/unconfirm`, { method: 'POST' }), 'Match undone, back to needs review');
  const removeException = (txnId: number) => act(() => apiFetchAuthed(`/reconciliation/transactions/${txnId}/exception`, { method: 'DELETE' }), 'Exception removed');
  // Dawn's inline category override (financial-only endpoint). Works on approved
  // receipts up to export; also clears a buyer's "Not sure" so finalize can proceed.
  const setReceiptCategory = (pid: string, categoryId: number) =>
    act(() => apiFetchAuthed(`/reconciliation/receipts/${pid}/category`, { method: 'PATCH', body: { category_id: categoryId } }), 'Category updated');
  // A refund's account lives on its exception (it has no receipt). Same lock as
  // the receipt overrides: editable until the period is exported.
  const setExceptionCategory = (txnId: number, categoryId: number) =>
    act(() => apiFetchAuthed(`/reconciliation/transactions/${txnId}/exception`, { method: 'PATCH', body: { category_id: categoryId } }), 'Account set on the refund');
  const setReceiptClass = (pid: string, classId: number) =>
    act(() => apiFetchAuthed(`/reconciliation/receipts/${pid}/class`, { method: 'PATCH', body: { purchase_class_id: classId } }), 'Class updated');
  const addException = (txnId: number, type: string) =>
    act(() => apiFetchAuthed(`/reconciliation/transactions/${txnId}/exception`, { method: 'POST', body: { exception_type: type } }), 'Marked as an exception')
      .then(() => setExceptFor(null));
  const markRecurring = (txnId: number, kind: RecurringKind, okMsg: string) =>
    act(() => apiFetchAuthed(`/reconciliation/transactions/${txnId}/recurring`, { method: 'POST', body: { kind } }), okMsg)
      .then(() => setRecurFor(null));
  const declineRecurring = (txnId: number, scope: 'charge' | 'vendor', okMsg: string) =>
    act(() => apiFetchAuthed(`/reconciliation/transactions/${txnId}/recurring/decline`, { method: 'POST', body: { scope } }), okMsg);
  const forgetRecurring = (memoryId: number) =>
    act(() => apiFetchAuthed(`/reconciliation/recurring/${memoryId}`, { method: 'DELETE' }), 'Forgotten. This charge is back in No receipt.');
  const setRecurringCategory = (txnId: number, categoryId: number) =>
    act(() => apiFetchAuthed(`/reconciliation/transactions/${txnId}/exception`, { method: 'PATCH', body: { category_id: categoryId } }), 'Account set on the subscription');
  const rematch = () =>
    selectedId && act(() => apiFetchAuthed(`/reconciliation/periods/${selectedId}/rematch`, { method: 'POST' }), 'Re-checked against current receipts');

  // A charge can have at most one picker open at a time; toggling closes it.
  // Opening a picker also dismisses any open exception editor on that row.
  const togglePicker = (txnId: number) =>
    setPickerFor((cur) => {
      const next = cur === txnId ? null : txnId;
      if (next != null) {
        setExceptFor((e) => (e === txnId ? null : e));
        setRecurFor((r) => (r === txnId ? null : r));
      }
      return next;
    });

  // Manual link: Dawn hand-picked a receipt for this charge. A manual link is her
  // confirmation, so this confirms the match + approves the receipt server-side.
  // A 409 (receipt already spoken for) surfaces via act()'s toast.
  const linkManual = async (txnId: number, receiptId: number) => {
    const ok = await confirmDialog({
      title: 'Link this receipt?',
      message: 'This confirms the match and approves the receipt.',
      confirmLabel: 'Link receipt',
    });
    if (!ok) return;
    await act(() => apiFetchAuthed('/reconciliation/matches', { method: 'POST', body: { card_transaction_id: txnId, receipt_id: receiptId } }), 'Receipt linked, match confirmed');
    setPickerFor(null);
  };

  // Per-section confirm: accept one cardholder group's confident matches at a time, so
  // Dawn reviews each section's classes (an "Auto" class she leaves is accepted as-is)
  // before confirming. Scoped to that group's charge ids.
  //
  // Under a search the button confirms exactly the rows on screen, which is the
  // right behavior but the wrong reading of "for Kevin Bisel" — so the dialog,
  // the last gate before a bulk write to financial records, says which it is.
  const confirmGroup = async (cardholder: string, txnIds: number[]) => {
    const n = txnIds.length;
    if (!selectedId || !n) return;
    const scope = filtering ? `the ${n} shown for ${cardholder}` : `${n} match${n === 1 ? '' : 'es'} for ${cardholder}`;
    const ok = await confirmDialog({
      title: `Confirm ${scope}?`,
      message: `Exact 1:1 matches (amount, card, and date line up). Confirming approves ${n} ${n === 1 ? 'receipt' : 'receipts'}${filtering ? ', only the ones your search is showing' : ''}. Review each row's class first — an "Auto" class you leave is accepted as-is.`,
      confirmLabel: `Confirm ${n}`,
    });
    if (!ok) return;
    act(() => apiFetchAuthed(`/reconciliation/periods/${selectedId}/confirm-confident`, { method: 'POST', body: { card_transaction_ids: txnIds } }), `Confirmed ${n} ${n === 1 ? 'match' : 'matches'}`);
  };

  // Like act(), but a 409 (still running, no end date, already exported) is an
  // answer about this key, so it stays beside it instead of vanishing in a toast.
  const lifecycle = async (step: 'finalize' | 'unfinalize', okMsg: string) => {
    setBusy(true);
    setLifecycleError(null);
    try {
      await apiFetchAuthed(`/reconciliation/periods/${selectedId}/${step}`, { method: 'POST', silent: true });
      await loadBoard(selectedId);
      toast.success(okMsg);
    } catch (e: any) {
      if (e?.status === 409) setLifecycleError(serverMessage(e, 'That cannot be done right now'));
      else toast.error(e?.message || 'Action failed');
    } finally { setBusy(false); }
  };

  const finalizePeriod = async () => {
    if (!selectedId) return;
    const ok = await confirmDialog({
      title: 'Finalize this period?',
      message: 'Locks the reconciliation and unlocks the export. Matches stay editable until you export or sync.',
      confirmLabel: 'Finalize period',
    });
    if (!ok) return;
    lifecycle('finalize', 'Period finalized');
  };

  const reopenPeriod = async () => {
    if (!selectedId) return;
    const ok = await confirmDialog({
      title: 'Reopen this period?',
      message: 'Undoes finalize so charges and matches can change again. Finalize it again when you are done.',
      confirmLabel: 'Reopen period',
    });
    if (!ok) return;
    lifecycle('unfinalize', 'Period reopened');
  };

  // Authed CSV download: fetch as a raw Response (skipJsonParse), then hand the
  // blob to a throwaway anchor. First download flips receipts/period to
  // exported server-side, so reload the board after.
  const downloadCsv = async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      const resp = (await apiFetchAuthed(`/reconciliation/periods/${selectedId}/export.csv`, { skipJsonParse: true })) as Response;
      const blob = await resp.blob();
      const cd = resp.headers.get('Content-Disposition') || '';
      const filename = /filename="?([^";]+)"?/i.exec(cd)?.[1] || `period-${selectedId}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      await loadBoard(selectedId);
    } catch (e: any) { toast.error(e?.message || 'Could not download the CSV'); }
    finally { setBusy(false); }
  };

  const syncQbo = async () => {
    if (!selectedId) return;
    const configured = !!board?.qbo?.configured;
    if (configured) {
      const ok = await confirmDialog({
        title: 'Sync to QuickBooks?',
        message: "Pushes this period's exported charges to QuickBooks as purchases.",
        confirmLabel: 'Sync to QBO',
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      const res: QboSyncResult = await apiFetchAuthed(`/reconciliation/periods/${selectedId}/sync-qbo`, { method: 'POST' });
      setQboResult(res);
      if (configured) {
        const n = res.synced ?? 0;
        const extras = [
          res.alreadySynced ? `${res.alreadySynced} already synced` : '',
          res.skipped?.length ? `${res.skipped.length} skipped` : '',
          res.errors?.length ? `${res.errors.length} failed` : '',
        ].filter(Boolean).join(' · ');
        toast.success(`Synced ${n} ${n === 1 ? 'purchase' : 'purchases'} to QBO${extras ? ` · ${extras}` : ''}`);
        await loadBoard(selectedId);
      }
    } catch (e: any) { toast.error(e?.message || 'QBO sync failed'); }
    finally { setBusy(false); }
  };

  const groups = useMemo(() => {
    const g: Record<Tier, ReconTxn[]> = { confident: [], review: [], noreceipt: [], refunds: [], recurring: [], done: [] };
    board?.transactions.forEach((t) => g[tierOf(t)].push(t));
    return g;
  }, [board]);

  // ── search: a lens, and ONLY a lens ────────────────────────────────────────
  // `visible` is what the piles render; `groups` stays whole and is what the rail,
  // the progress bar and the finalize gate read. Collapsing that distinction is
  // the bug this feature is one line away from at all times: `fullyDone` keys off
  // groups.confident.length, so filtering in place would let a query that hides
  // the last unconfirmed match unlock Finalize on a period nobody reconciled.
  const visible = useMemo(() => {
    if (!filtering) return groups;
    const f: Record<Tier, ReconTxn[]> = { confident: [], review: [], noreceipt: [], refunds: [], recurring: [], done: [] };
    (Object.keys(groups) as Tier[]).forEach((k) => {
      f[k] = groups[k].filter((t) => txnMatchesQuery(t, query));
    });
    return f;
  }, [groups, query, filtering]);

  // One person can hold two cards. The check is the person, so both rows share it.
  const noreceiptPeople = useMemo(() => {
    const byId = new Map<string, string>();
    groupByCard(visible.noreceipt).forEach(([cardholder, rows]) => {
      const row = rows.find((r) => r.card_owner_user_id);
      if (!row?.card_owner_user_id || byId.has(row.card_owner_user_id)) return;
      byId.set(row.card_owner_user_id, row.card_owner_name || cardholder);
    });
    return Array.from(byId, ([userId, name]) => ({ userId, name }));
  }, [visible.noreceipt]);
  const pickedCount = noreceiptPeople.filter((p) => picked.has(p.userId)).length;
  // A flagged charge can sit in Needs your eyes (a recurring hint) as well as No
  // receipt, so each of those headers counts its own visible rows.
  const flagged = useMemo(() => ({
    review: visible.review.filter(isFlagged).length,
    noreceipt: visible.noreceipt.filter(isFlagged).length,
  }), [visible.review, visible.noreceipt]);
  useEffect(() => {
    const live = new Set(noreceiptPeople.map((p) => p.userId));
    setPicked((prev) => {
      let changed = false;
      const next = new Set<string>();
      prev.forEach((id) => { if (live.has(id)) next.add(id); else changed = true; });
      return changed ? next : prev;
    });
  }, [noreceiptPeople]);
  const nudgeChecked = async () => {
    const targets = noreceiptPeople.filter((p) => picked.has(p.userId));
    const { sentIds } = await sendSelected(targets, selectedId);
    if (!sentIds.length) return;
    setPicked((prev) => {
      const next = new Set(prev);
      sentIds.forEach((id) => next.delete(id));
      return next;
    });
  };

  // What the query found, and what it comes to. The sum answers the question a
  // bookkeeper actually has after searching a vendor: how much did we spend there
  // this month. Negated like the rail's own figure, so purchases read positive.
  const found = useMemo(() => {
    const rows = (Object.keys(visible) as Tier[]).flatMap((k) => visible[k]);
    return { count: rows.length, total: rows.reduce((s, t) => s - (Number(t.amount) || 0), 0) };
  }, [visible]);

  // Keyboard-driven review. Rows below take `focused` from this; the hook owns the
  // cursor, auto-advance, auto-expand/scroll, and the single window keydown listener.
  const { focusedId, helpOpen, setHelpOpen } = useKeyboardReview({
    groups: visible, // the cursor walks what the query surfaced, not the whole period
    selectedId,
    busy,
    peekOpen: peekPid != null,
    pickerOpen: pickerFor != null,
    exceptionOpen: exceptFor != null || recurFor != null,
    query,
    clearQuery: () => setQuery(''),
    searchRef,
    setOpen,
    setOpenGroups,
    confirmMatch: confirmOne,
    rejectMatch: rejectOne,
    openPicker: togglePicker,
    openException: (txnId: number) => { setPickerFor(null); setRecurFor(null); setExceptFor(txnId); },
    openPeek: setPeekPid,
    closePeek: () => setPeekPid(null),
    closePicker: () => setPickerFor(null),
    closeException: () => { setExceptFor(null); setRecurFor(null); },
  });

  // Distinct card last4s in this period with no company_cards row (card_label
  // null) — charges on them can't attribute to a person until set up in the manager.
  const unmanagedLast4s = useMemo(() => {
    const s = new Set<string>();
    board?.transactions.forEach((t) => { if (!t.card_label && t.card_last4) s.add(t.card_last4); });
    return Array.from(s);
  }, [board]);

  // Dollars still in play (everything not yet in the Done pile). The bank-rec "get
  // this to $0" target — bulk-confirming or excepting charges visibly ticks it down.
  // Amounts are signed (purchases negative, credits positive), so a credit
  // offsets the total instead of inflating it.
  const unreconciled = useMemo(() => {
    let sum = 0;
    board?.transactions.forEach((txn) => {
      const tier = tierOf(txn);
      if (tier === 'done' || tier === 'recurring') return;
      if (tier === 'refunds' && txn.exception) return; // resolved refund — settled, same as done
      sum -= Number(txn.amount) || 0;
    });
    return sum;
  }, [board]);

  // Rail pile-count → open that section and scroll to it. Works from a collapsed
  // pile because only the section's BODY toggles, never the <section> itself.
  //
  // A search does unmount empty piles, though, and the rail keeps showing the
  // period's real counts — so jumping to a pile the query emptied would scroll
  // to nothing. The count she clicked is the unfiltered one, so clicking it means
  // "show me those": drop the query rather than leave a dead button.
  const jumpTo = (tier: Tier) => {
    setQuery('');
    setOpen((o) => ({ ...o, [tier]: true }));
    requestAnimationFrame(() => document.getElementById(`sec-${tier}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  const t = board?.tally;
  const needsYou = (t?.review || 0) + (t?.unmatched || 0);
  // Resolved = the "Done" pile (confirmed matches + exceptions), what the progress bar fills to.
  const resolved = (t?.matched || 0) + (t?.exceptions || 0);
  const doneExceptions = (t?.exceptions || 0) - (t?.recurring || 0);
  // Nothing left in play: no review/unmatched AND no still-unconfirmed confident
  // matches. Note: with signed sums, an offsetting open purchase+credit pair can read
  // $0.00 before everything is resolved, so the green state keys on THIS, not the sum.
  const fullyDone = needsYou === 0 && groups.confident.length === 0 && (t?.total || 0) > 0;
  // Matched receipts still missing a category or a purchase class (a buyer/scanner
  // "Not sure", or blank) — the finalize/export gate blocks on these, so surface the
  // count and hold Finalize until Dawn sets them. Kept separate from fullyDone: these
  // charges ARE resolved (matched), just incomplete, so they don't affect the rail.
  const needsTagCount = (board?.transactions || []).filter((x) => x.match && (x.match.receipt_category_id == null || x.match.receipt_purchase_class_id == null)).length;
  // A subscription's account lives on its exception (and the memory learns it): finalize holds until every recurring row has one.
  const recurringUntagged = (board?.transactions || []).filter((x) => x.exception?.exception_type === 'recurring' && x.exception.category_id == null).length;

  // Section-specific right-hand action for a charge row.
  // Explicit receipt-peek affordance on matched rows (the paired cells also open
  // it on click, but a labeled button is the discoverable path).
  const viewReceiptBtn = (txn: ReconTxn) => txn.match ? (
    <button onClick={() => setPeekPid(txn.match!.receipt_public_id)}
      className={buttonClass('quiet', 'sm')}>view receipt</button>
  ) : null;

  // A "Needs your eyes" row that is there for a subscription question, not a match.
  // The filled button is the answer the evidence leans toward.
  const recurringReviewAction = (txn: ReconTxn) => {
    const h = txn.recurring_hint;
    const p = txn.recurring_proposal;
    if (!h && !p) return null;
    const findBtn = (
      <button onClick={() => togglePicker(txn.id)} disabled={busy} className={buttonClass('quiet', 'sm')}>
        {pickerFor === txn.id ? 'close' : 'find its receipt'}
      </button>
    );
    const separate = (filled: boolean) => (
      <Tooltip label="Not the subscription. It goes to No receipt like any other charge">
        <button onClick={() => declineRecurring(txn.id, 'charge', 'Kept as a separate purchase')} disabled={busy}
          className={filled ? buttonClass('filled', 'sm') : buttonClass('quiet', 'sm')}>Separate purchase</button>
      </Tooltip>
    );
    const notSub = (
      <Tooltip label="Never suggest this vendor on this card as a subscription again">
        <button onClick={() => declineRecurring(txn.id, 'vendor', 'Got it, not a subscription')} disabled={busy}
          className={buttonClass('danger-quiet', 'sm')}>Not a subscription</button>
      </Tooltip>
    );
    if (h?.reason === 'amount_changed') return (
      <div className="flex items-center gap-3">
        <Tooltip label="The price changed. Remember this amount from now on">
          <button onClick={() => markRecurring(txn.id, h.kind, 'Subscription updated to the new price')} disabled={busy}
            className={buttonClass('filled', 'sm')}>Subscription changed</button>
        </Tooltip>
        {separate(false)}
        {findBtn}
      </div>
    );
    if (h?.reason === 'sibling_declined') return (
      <div className="flex items-center gap-3">
        <Tooltip label="This charge is the subscription this period">
          <button onClick={() => markRecurring(txn.id, h.kind, 'Remembered as a subscription')} disabled={busy}
            className={buttonClass('filled', 'sm')}>Yes, this is it</button>
        </Tooltip>
        {separate(false)}
        {notSub}
        {findBtn}
      </div>
    );
    if (h) return (
      <div className="flex items-center gap-3">
        {separate(true)}
        {notSub}
        {findBtn}
      </div>
    );
    const same = p!.same_amount;
    return (
      <div className="flex items-center gap-3">
        <Tooltip label="Future charges from this vendor on this card at this price resolve themselves">
          <button onClick={() => markRecurring(txn.id, 'fixed', 'Remembered as a subscription')} disabled={busy}
            className={same ? buttonClass('filled', 'sm') : buttonClass('quiet', 'sm')}>Recurring, same amount</button>
        </Tooltip>
        <Tooltip label="Future charges from this vendor on this card resolve themselves at any price">
          <button onClick={() => markRecurring(txn.id, 'variable', 'Remembered as a subscription')} disabled={busy}
            className={same ? buttonClass('quiet', 'sm') : buttonClass('filled', 'sm')}>Recurring, amount varies</button>
        </Tooltip>
        {notSub}
        {findBtn}
      </div>
    );
  };

  const rowAction = (tier: Tier) => (txn: ReconTxn) => {
    if (tier === 'confident') return (
      <div className="flex items-center gap-3">
        {viewReceiptBtn(txn)}
        <button onClick={() => txn.match && rejectOne(txn.match.id)} disabled={busy} className={buttonClass('danger-quiet', 'sm')}>not this</button>
      </div>
    );
    if (tier === 'review') return txn.match ? (
      <div className="flex items-center gap-3">
        {viewReceiptBtn(txn)}
        <button onClick={() => confirmOne(txn.match!.id)} disabled={busy}
          className={buttonClass('filled', 'sm')}>Confirm</button>
        <button onClick={() => rejectOne(txn.match!.id)} disabled={busy} className={buttonClass('danger-quiet', 'sm')}>reject</button>
        <button onClick={() => togglePicker(txn.id)} disabled={busy} className={buttonClass('quiet', 'sm')}>
          {pickerFor === txn.id ? 'close' : 'pick another'}
        </button>
      </div>
    ) : recurringReviewAction(txn);
    if (tier === 'noreceipt' && recurFor === txn.id) return (
      <div className="flex items-center gap-3">
        <Tooltip label="Future charges from this vendor on this card at this price resolve themselves">
          <button autoFocus onClick={() => markRecurring(txn.id, 'fixed', 'Remembered as a subscription')} disabled={busy}
            className={buttonClass('quiet', 'sm')}>Same amount each time</button>
        </Tooltip>
        <Tooltip label="Future charges from this vendor on this card resolve themselves at any price">
          <button onClick={() => markRecurring(txn.id, 'variable', 'Remembered as a subscription')} disabled={busy}
            className={buttonClass('quiet', 'sm')}>Amount varies</button>
        </Tooltip>
        <button onClick={() => setRecurFor(null)} className={buttonClass('quiet', 'sm')}>cancel</button>
      </div>
    );
    if (tier === 'noreceipt') return exceptFor === txn.id ? (
      <div className="flex items-center gap-2">
        <Select label="Exception reason" size="sm" width="fit" autoFocus value={null} placeholder="Reason…"
          options={EXCEPTION_TYPES.map((e) => ({ key: e.value, label: e.label }))}
          onChange={(k) => addException(txn.id, k)} />
        <button onClick={() => setExceptFor(null)} className={buttonClass('quiet', 'sm')}>cancel</button>
      </div>
    ) : (
      <div className="flex items-center gap-3">
        <button onClick={() => togglePicker(txn.id)} className={buttonClass('quiet', 'sm')}>
          {pickerFor === txn.id ? 'close' : 'find its receipt'}
        </button>
        <button onClick={() => { setPickerFor(null); setRecurFor(null); setExceptFor(txn.id); }} className={buttonClass('quiet', 'sm')}>mark exception</button>
        <Tooltip label="Remember this vendor on this card as a subscription, so later charges resolve themselves">
          <button onClick={() => { setPickerFor(null); setExceptFor(null); setRecurFor(txn.id); }}
            className={buttonClass('quiet', 'sm')}>recurring</button>
        </Tooltip>
      </div>
    );
    if (tier === 'refunds') return txn.exception ? (
      <div className="flex items-center gap-3">
        <Tag tone="warn">
          {exceptionTypeLabel(txn.exception?.exception_type) || 'exception'}
        </Tag>
        <button onClick={() => removeException(txn.id)} disabled={busy} className={buttonClass('danger-quiet', 'sm')}>undo</button>
      </div>
    ) : exceptFor === txn.id ? (
      <div className="flex items-center gap-2">
        <Select label="Exception reason" size="sm" width="fit" autoFocus value={null} placeholder="Reason…"
          options={EXCEPTION_TYPES.map((e) => ({ key: e.value, label: e.label }))}
          onChange={(k) => addException(txn.id, k)} />
        <button onClick={() => setExceptFor(null)} className={buttonClass('quiet', 'sm')}>cancel</button>
      </div>
    ) : (
      // Post-undo state: credits never get a "find its receipt" picker, only a
      // way to record a different exception type (e.g. Disputed).
      <button onClick={() => setExceptFor(txn.id)} className={buttonClass('quiet', 'sm')}>mark exception</button>
    );
    if (tier === 'recurring') return (
      <div className="flex items-center gap-3">
        <Tag>recurring</Tag>
        <Tooltip label="This charge is a separate purchase. It goes back to No receipt; the subscription stays remembered">
          <button onClick={() => declineRecurring(txn.id, 'charge', 'Back in No receipt')} disabled={busy}
            className={buttonClass('danger-quiet', 'sm')}>not this one</button>
        </Tooltip>
        <Tooltip label="Stop treating this vendor on this card as a subscription. This charge goes back to No receipt">
          <button onClick={() => txn.recurring_memory_id != null && forgetRecurring(txn.recurring_memory_id)} disabled={busy || txn.recurring_memory_id == null}
            className={buttonClass('danger-quiet', 'sm')}>stop remembering</button>
        </Tooltip>
      </div>
    );
    // done
    return (
      <div className="flex items-center gap-3">
        {txn.exception ? (
          <Tag tone="warn">
            {exceptionTypeLabel(txn.exception?.exception_type) || 'exception'}
          </Tag>
        ) : txn.match ? (
          <Tooltip label="Peek at this receipt">
            <button onClick={() => setPeekPid(txn.match!.receipt_public_id)}
              className={buttonClass('quiet', 'sm')}>
              ✓ {txn.match.vendor_name || 'matched'}
            </button>
          </Tooltip>
        ) : (
          <span className="text-[11px] text-green-400/80">✓ matched</span>
        )}
        <button
          onClick={() => (txn.exception ? removeException(txn.id) : txn.match && unconfirmOne(txn.match.id))}
          disabled={busy}
          className={buttonClass('danger-quiet', 'sm')}
        >
          undo
        </button>
      </div>
    );
  };

  // The "find its receipt" expansion, rendered under a charge row when its picker
  // is open. Used by the review + no-receipt piles.
  const renderPicker = (txn: ReconTxn) =>
    pickerFor === txn.id ? (
      <MatchPicker
        txn={txn}
        busy={busy}
        onLink={(receiptId) => linkManual(txn.id, receiptId)}
        onPeek={setPeekPid}
        onMarkException={() => { setPickerFor(null); setExceptFor(txn.id); }}
      />
    ) : null;

  // Dawn's inline expense-category control, rendered under any matched row (every
  // pile — a deferred receipt can be confident, and moves to Done once confirmed).
  // Editable only while the category can still change server-side: the override
  // endpoint stops at export, so once the period is exported/synced we don't
  // offer a control that would 409.
  const categoryEditable = board?.period.status === 'open' || board?.period.status === 'reconciled';
  // Category + purchase class, STACKED in the row's right cluster (no extra row width).
  // The request chip rides above the tags, and stays after export when the tags lock.
  const renderCategory = (txn: ReconTxn) => {
    const m = txn.match;
    if (!m) return null;
    const chips = m.requisitions && m.requisitions.length ? <RequisitionChips requisitions={m.requisitions} /> : null;
    if (!categoryEditable) return chips;
    const tags = (
      // Faint bordered container groups the two dropdowns into one tidy unit so the
      // row reads as [evidence] [tags] [actions] instead of loose stacked selects.
      <div className="flex flex-col gap-1.5 rounded-md bg-surface-container-high px-2 py-1.5">
        <InlineOverride prefix="Category" ariaLabel="Expense category"
          options={categories} value={m.receipt_category_id ?? null} deferred={!!m.receipt_category_deferred}
          busy={busy} onSet={(id) => setReceiptCategory(m.receipt_public_id, id)}
          setPlaceholder="Set category…" deferredPlaceholder="Pick a category" />
        <InlineOverride prefix="Bought for" ariaLabel="Purchase class"
          options={classes} value={m.receipt_purchase_class_id ?? null} deferred={!!m.receipt_class_deferred}
          autoPicked={m.receipt_class_source === 'auto'}
          busy={busy} onSet={(id) => setReceiptClass(m.receipt_public_id, id)}
          setPlaceholder="Set class…" deferredPlaceholder="Pick a class" />
      </div>
    );
    return chips ? <div className="flex flex-col items-end gap-1.5 min-w-0">{chips}{tags}</div> : tags;
  };

  // The Refunds pile's account control. A credit has no receipt to carry an
  // account, so the picker writes to the exception, and the sentence under it
  // offers the purchase this credit reverses: one click adopts that account. The
  // caption's wording carries the confidence (same amount vs merchant only).
  // A subscription's account lives on its exception, like a refund's.
  const renderRecurringCategory = (txn: ReconTxn) => {
    const e = txn.exception;
    if (!e || !categoryEditable) return null;
    return (
      <div className="flex flex-col gap-1.5 rounded-md bg-surface-container-high px-2 py-1.5">
        <InlineOverride prefix="Account" ariaLabel="Subscription expense account"
          options={categories} value={e.category_id ?? null} deferred={false}
          busy={busy} onSet={(id) => setRecurringCategory(txn.id, id)}
          setPlaceholder="Set account…" deferredPlaceholder="Pick an account" />
      </div>
    );
  };

  const renderRefundCategory = (txn: ReconTxn) => {
    const e = txn.exception;
    if (!e || !categoryEditable) return null;
    const s = txn.refund_suggestion;
    const adopted = suggestionAdopted(s, e.category_id);
    return (
      <div className="flex flex-col gap-1.5 rounded-md bg-surface-container-high px-2 py-1.5">
        <InlineOverride prefix="Account" ariaLabel="Refund expense account"
          options={categories} value={e.category_id ?? null} deferred={false}
          busy={busy} onSet={(id) => setExceptionCategory(txn.id, id)}
          setPlaceholder="Set account…" deferredPlaceholder="Pick an account" />
        {s && (
          <div className="flex items-center gap-2 min-w-0 text-[11px]">
            <span className="min-w-0 truncate text-on-surface-variant" title={refundSuggestionCaption(s, formatEventDate)}>
              {refundSuggestionCaption(s, formatEventDate)}
              {s.category_label ? <> · <span className="text-on-surface">{s.category_label}</span></> : null}
            </span>
            {adopted ? (
              <span className="shrink-0 text-green-400/80">✓ using it</span>
            ) : (
              <button type="button" disabled={busy} onClick={() => setExceptionCategory(txn.id, s.category_id)}
                className={`${buttonClass('quiet', 'sm')} shrink-0`}>
                Use {s.category_label || 'this account'}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <PageLayout width="full" kind="workspace" className="pt-4 lg:pt-6" header={(
      <PageHeader className="mb-6" title="Reconcile" back={{ to: "/reconcile", label: "Reconcile" }} />
    )}>
      <div className="grid gap-6 lg:grid-cols-[300px_minmax(0,1fr)]">
          {/* ── Control rail: current period, difference-to-$0, jump-list, actions ──── */}
          <aside className="lg:sticky lg:top-4 self-start space-y-4">
            {/* Current period: the identity of what you're working inside */}
            <Slot gap="4" show={!!board}>
              {board && (
                <Panel label="This period">
                  <PeriodIdentity board={board} onCloseChanged={() => loadBoard(selectedId)} />
                </Panel>
              )}
            </Slot>

            {/* Difference-to-zero — the bank-rec finish line; turns green at $0.00 */}
            <Slot gap="4" show={!!t && t.total > 0}>
            {t && t.total > 0 && (
              <Panel label="Left to reconcile" className={`transition-shadow duration-standard motion-reduce:transition-none ${fullyDone ? 'ring-1 ring-inset ring-green-600/40' : ''}`}>
                <div className="flex items-center gap-2">
                  <span className={`text-2xl font-bold font-mono tabular-nums transition-colors duration-500 ${fullyDone ? 'text-green-300' : 'text-on-surface'}`}>
                    {money(unreconciled)}
                  </span>
                  <Appear show={fullyDone}>
                    <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </Appear>
                </div>
                <div className="text-xs text-on-surface-variant mt-0.5">{fullyDone ? 'All reconciled' : 'left to reconcile'}</div>
                <Progress className="mt-3" label="Charges cleared" value={resolved} max={t.total} success={fullyDone} />
                <div className="text-[11px] text-on-surface-variant mt-1.5">{resolved} of {t.total} cleared</div>
              </Panel>
            )}
            </Slot>

            {/* Statement lifecycle: finalize → export → sync to QBO */}
            {board && (
              <Panel title="Lifecycle" level={3} padding="sm" className="gap-2">
                <FadeIn key={board.period.status === 'open' ? 'open' : 'closed'} className="flex flex-col gap-2">
                {board.period.status === 'open' ? (
                  <>
                    <div className="[&>span]:flex [&>span]:w-full"><Tooltip label={fullyDone ? (needsTagCount > 0 ? 'Set a category and class on every receipt first' : recurringUntagged > 0 ? 'Set an account on every recurring charge first' : 'Lock this period and unlock the export') : 'Resolve every charge first'}>
                      <button onClick={finalizePeriod} disabled={busy || !fullyDone || needsTagCount > 0 || recurringUntagged > 0}
                        className={`${buttonClass('filled', 'md')} w-full`}>
                        Finalize period
                      </button>
                    </Tooltip></div>
                    <Reveal show={!!lifecycleError}><Alert variant="text" className="justify-center text-center text-xs">{lifecycleError}</Alert></Reveal>
                    <Reveal show={!fullyDone}><p className="text-[11px] text-on-surface-variant text-center">resolve every charge first</p></Reveal>
                    <Reveal show={fullyDone && needsTagCount > 0}>
                      <p className="text-[11px] text-amber-400/90 text-center">
                        {needsTagCount} receipt{needsTagCount === 1 ? '' : 's'} need a category or class — set them first
                      </p>
                    </Reveal>
                    <Reveal show={fullyDone && recurringUntagged > 0}>
                      <p className="text-[11px] text-amber-300/90 text-center">
                        {recurringUntagged} recurring charge{recurringUntagged === 1 ? ' needs' : 's need'} an account, set it on the Recurring pile first
                      </p>
                    </Reveal>
                  </>
                ) : (
                  <>
                    <Tag tone="good" className="self-start">
                      {board.period.status === 'reconciled' ? 'Finalized' : board.period.status === 'exported' ? 'Exported' : 'Synced to QBO'}
                    </Tag>
                    <button onClick={downloadCsv} disabled={busy}
                      className={`${buttonClass('filled', 'md')} w-full`}>
                      Download CSV
                    </button>
                    {/* QBO push is dormant until credentials are configured (the export
                        shape is still an open question with the accountant) — the
                        workflow deliberately ends at Download CSV until then. */}
                    <Reveal show={board.period.status !== 'synced' && !!board.qbo?.configured}>
                      <button onClick={syncQbo} disabled={busy}
                        className={`${buttonClass('quiet', 'md')} w-full`}>
                        Sync to QBO
                      </button>
                    </Reveal>
                    <Reveal show={!!qboResult}>
                      {qboResult && <QboResultPanel result={qboResult} onDismiss={() => setQboResult(null)} />}
                    </Reveal>
                    <Reveal show={board.period.status === 'reconciled'}>
                      <div className="[&>span]:flex [&>span]:w-full"><Tooltip label="Undo finalize so charges can change. Not possible once exported.">
                        <button onClick={reopenPeriod} disabled={busy}
                          className={`${buttonClass('quiet', 'sm')} w-full`}>
                          Reopen period
                        </button>
                      </Tooltip></div>
                    </Reveal>
                    <Reveal show={!!lifecycleError}><Alert variant="text" className="justify-center text-center text-xs">{lifecycleError}</Alert></Reveal>
                  </>
                )}
                </FadeIn>
              </Panel>
            )}

            {/* Pile jump-list */}
            <Slot gap="4" show={!!t}>
            {t && (
              <Panel label="Jump to a pile" padding="none" className="p-1.5">
                {([
                  { tier: 'confident' as Tier, label: 'Confident', dot: 'bg-green-400', count: groups.confident.length },
                  { tier: 'review' as Tier, label: 'Needs your eyes', dot: 'bg-amber-400', count: groups.review.length },
                  { tier: 'noreceipt' as Tier, label: 'No receipt', dot: 'bg-outline', count: groups.noreceipt.length },
                  { tier: 'refunds' as Tier, label: 'Refunds', dot: 'bg-outline', count: groups.refunds.length },
                  { tier: 'recurring' as Tier, label: 'Recurring', dot: 'bg-outline', count: groups.recurring.length },
                  { tier: 'done' as Tier, label: 'Done', dot: 'bg-outline-variant', count: groups.done.length },
                ]).map((r) => (
                  <button key={r.tier} onClick={() => jumpTo(r.tier)} disabled={!r.count}
                    className={`np-state w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-left text-sm disabled:opacity-60 ${FOCUS}`}>
                    <span aria-hidden="true" className={`w-1.5 h-1.5 rounded-full ${r.dot}`} />
                    <span className="text-on-surface">{r.label}</span>
                    <span className="ml-auto font-mono tabular-nums text-on-surface-variant">{r.count}</span>
                  </button>
                ))}
              </Panel>
            )}
            </Slot>

            {/* Keyboard hint — quiet one-liner; ? opens the full map */}
            {t && (
              <div className="relative px-1">
                <button type="button" onClick={() => setHelpOpen(!helpOpen)} aria-expanded={helpOpen}
                  className={`rounded text-left text-[11px] text-on-surface-variant hover:text-on-surface transition-colors leading-relaxed ${FOCUS}`}>
                  Keyboard: j/k move · y confirm · r reject · e exception · f find · p peek · <span className="text-on-surface">/</span> search · <span className="text-on-surface">?</span> help
                </button>
                <Appear show={helpOpen} as="div" className="absolute z-30 mt-1 w-full rounded-lg bg-surface-container-high shadow-[var(--np-overlay-shadow)] p-3 space-y-1.5">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-on-surface-variant">Keyboard</span>
                      <Tooltip label="Close">
                        <IconButton size="sm" label="Close" onClick={() => setHelpOpen(false)}>
                          <IconClose />
                        </IconButton>
                      </Tooltip>
                    </div>
                    {([
                      ['j / k', 'move between charges that need you'],
                      ['↑ / ↓', 'same as j / k'],
                      ['y or Enter', 'confirm the suggested match'],
                      ['r', 'reject the suggestion'],
                      ['e', 'mark an exception (no-receipt charges)'],
                      ['f', 'find its receipt'],
                      ['p', 'peek at the matched receipt'],
                      ['/', 'jump to search'],
                      ['Esc', 'clear the search, close a panel, or clear the cursor'],
                      ['?', 'toggle this help'],
                    ] as [string, string][]).map(([k, d]) => (
                      <div key={k} className="flex items-baseline gap-2 text-xs">
                        <kbd className="shrink-0 min-w-[4rem] text-center rounded bg-surface-container-highest px-1.5 py-0.5 font-mono text-[11px] text-on-surface">{k}</kbd>
                        <span className="text-on-surface-variant">{d}</span>
                      </div>
                    ))}
                </Appear>
              </div>
            )}

            {/* Actions (import, delete, manage-cards and auto-confirm all live on the library) */}
            <div className="space-y-2">
              <div className="[&>span]:flex [&>span]:w-full"><Tooltip label="Re-check unmatched charges against receipts added or corrected since the import">
                <button onClick={rematch} disabled={busy || !validId}
                  className={`${buttonClass('tonal', 'md')} w-full`}>
                  Re-run matching
                </button>
              </Tooltip></div>
            </div>
          </aside>

          {/* ── Board ──────────────────────────────────────────────────────── */}
          <main className="min-w-0 space-y-4">
            {/* Unmanaged-card nudge: a last4 in this period with no card record */}
            <Slot gap="4" show={unmanagedLast4s.length > 0}>
              <Alert
                tone="warning"
                title={`${unmanagedLast4s.length} unmanaged card${unmanagedLast4s.length === 1 ? '' : 's'} in this period`}
                action={(
                  <Link to="/admin/cards" className={buttonClass('tonal', 'md')}>
                    Assign cards
                  </Link>
                )}
              >
                (<span className="font-mono tabular-nums">{unmanagedLast4s.slice(0, 5).map((l) => `•• ${l}`).join(', ')}{unmanagedLast4s.length > 5 ? '…' : ''}</span>).
                {' '}Charges on {unmanagedLast4s.length === 1 ? 'it' : 'them'} can't be tied to a person yet.
              </Alert>
            </Slot>

            {/* Search over every pile. Nothing here names a category or a vendor:
                both products run this page against their own chart of accounts. */}
            <Slot gap="4" show={!!board && board.tally.total > 0}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 p-0.5">
                <SearchField
                  ref={searchRef}
                  value={query}
                  onChange={setQuery}
                  placeholder="Search charges, amounts, vendors"
                  label="Search the charges in this period"
                  className="flex-1 min-w-[16rem]"
                />
                <FadeIn key={filtering ? 'found' : 'hint'}>
                {filtering ? (
                  // Status message (WCAG 4.1.3): the count is the only feedback that
                  // typing did anything. Also covers the zero case, so the empty state
                  // below deliberately does NOT get a second live region.
                  <span role="status" aria-live="polite" className="text-sm text-on-surface-variant tabular-nums">
                    {found.count} charge{found.count === 1 ? '' : 's'}
                    {/* Re-signed to the row convention, so a vendor that only issued a
                        refund reads "+$50.00" and not as if we spent it. */}
                    {found.count > 0 && <> · <span className="font-mono text-on-surface">{txnMoney(-found.total)}</span></>}
                  </span>
                ) : (
                  <span className="text-[11px] text-on-surface-variant">
                    press <kbd className="rounded bg-surface-container-highest px-1.5 py-0.5 font-mono text-[10px] text-on-surface">/</kbd> to search
                  </span>
                )}
                </FadeIn>
              </div>
            </Slot>

            <FadeIn key={busy && !board ? 'loading' : board ? 'board' : 'none'} className="space-y-4">
            {busy && !board ? (
              <SkeletonGroup label="Loading the board" className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14 rounded-panel" />)}</SkeletonGroup>
            ) : board ? (
              <>
                <Slot gap="4" show={importChecks.length > 0 && !filtering}>
                {(() => {
                  const single = importChecks.length === 1 ? importChecks[0].import_summary! : null;
                  const unknown = importChecks.some((u) => (u.import_summary?.unknown_cards.length || 0) > 0
                    || (u.import_summary?.into_finalized?.count || 0) > 0);
                  return (
                    <Section id="sec-importcheck" title="Import check"
                      count={single ? single.kept : importChecks.length}
                      badge={unknown ? 'amber' : 'gray'}
                      hint={single
                        ? `${single.kept} of ${single.file_rows} file rows read, ${formatEventDate(single.period_start)} to ${formatEventDate(single.period_end)}`
                        : `${importChecks.length} statements brought in this period's charges`}
                      open={importOpen} onToggle={() => setImportOpen((v) => !v)}>
                      <p className="px-4 py-2.5 text-sm text-on-surface-variant">
                        How each statement file was handled at import. Nothing here needs action unless it is amber.
                      </p>
                      {importChecks.map((u) => (
                        <ImportCheckBody key={u.id} summary={u.import_summary!} name={single ? null : (u.source_filename || u.label || 'Statement')} />
                      ))}
                    </Section>
                  );
                })()}
                </Slot>
                <Slot gap="4" show={visible.confident.length > 0}>
                  <Section id="sec-confident" title="Confident matches" count={visible.confident.length} total={groups.confident.length} badge="green"
                    hint="exact amount, card and date — confirm a cardholder at a time" open={filtering || open.confident} onToggle={() => toggle('confident')}>
                    <GroupedBody tier="confident" txns={visible.confident} tint="confident" openGroups={openGroups} onToggle={toggleCard} forceOpen={filtering} focusedId={focusedId} renderAction={rowAction('confident')} renderCategory={renderCategory} paired onPeek={setPeekPid} onConfirmGroup={confirmGroup} busy={busy} />
                  </Section>
                </Slot>

                <Slot gap="4" show={visible.review.length > 0}>
                  <Section id="sec-review" title="Needs your eyes" count={visible.review.length} total={groups.review.length} badge="amber" tone="attention"
                    hint="fuzzy, or more than one possible match" open={filtering || open.review} onToggle={() => toggle('review')}
                    actions={flagged.review > 0 ? <FlaggedCount n={flagged.review} /> : undefined}>
                    <GroupedBody tier="review" txns={visible.review} tint="review" openGroups={openGroups} onToggle={toggleCard} forceOpen={filtering} focusedId={focusedId} renderAction={rowAction('review')} renderExpansion={renderPicker} renderCategory={renderCategory} renderNote={recurringSentence} paired onPeek={setPeekPid} />
                  </Section>
                </Slot>

                <Slot gap="4" show={visible.noreceipt.length > 0}>
                  <Section id="sec-noreceipt" title="No receipt" count={visible.noreceipt.length} total={groups.noreceipt.length} badge="gray"
                    hint="who owes one" open={filtering || open.noreceipt} onToggle={() => toggle('noreceipt')}
                    actions={(
                      <div className="flex items-center gap-3">
                        {flagged.noreceipt > 0 && <FlaggedCount n={flagged.noreceipt} />}
                        <NudgeSelectedButton count={pickedCount} sending={nudgeSending} progress={nudgeProgress} onClick={nudgeChecked} />
                      </div>
                    )}>
                    <GroupedBody tier="noreceipt" txns={visible.noreceipt} tint="none" openGroups={openGroups} onToggle={toggleCard} forceOpen={filtering} focusedId={focusedId} renderAction={rowAction('noreceipt')} renderExpansion={renderPicker}
                      nudgeRecent={nudgeRecent} picked={picked} onTogglePick={togglePick} pickDisabled={nudgeSending} />
                  </Section>
                </Slot>

                <Slot gap="4" show={visible.refunds.length > 0}>
                  <Section id="sec-refunds" title="Refunds" count={visible.refunds.length} total={groups.refunds.length} badge="gray" tone="muted"
                    hint="credits auto-resolve, never matched to receipts" open={filtering || open.refunds} onToggle={() => toggle('refunds')}>
                    <GroupedBody tier="refunds" txns={visible.refunds} tint="done" openGroups={openGroups} onToggle={toggleCard} forceOpen={filtering} focusedId={focusedId} renderAction={rowAction('refunds')} renderCategory={renderRefundCategory} />
                  </Section>
                </Slot>

                <Slot gap="4" show={visible.recurring.length > 0}>
                  <Section id="sec-recurring" title="Recurring" count={visible.recurring.length} total={groups.recurring.length} badge="gray" tone="muted"
                    hint="subscriptions auto-resolve, undo any that are wrong" open={filtering || open.recurring} onToggle={() => toggle('recurring')}>
                    <GroupedBody tier="recurring" txns={visible.recurring} tint="done" openGroups={openGroups} onToggle={toggleCard} forceOpen={filtering} focusedId={focusedId} renderAction={rowAction('recurring')} renderCategory={renderRecurringCategory} />
                  </Section>
                </Slot>

                <Slot gap="4" show={visible.done.length > 0}>
                  <Section id="sec-done" title="Done" count={visible.done.length} total={groups.done.length} badge="gray" tone="muted"
                    hint={`${t?.matched ?? 0} matched, ${doneExceptions} exception${doneExceptions === 1 ? '' : 's'}${t?.autoConfirmed ? `, ${t.autoConfirmed} auto-confirmed` : ''}`}
                    open={filtering || open.done} onToggle={() => toggle('done')}>
                    <GroupedBody tier="done" txns={visible.done} tint="done" openGroups={openGroups} onToggle={toggleCard} forceOpen={filtering} focusedId={focusedId} renderAction={rowAction('done')} renderCategory={renderCategory} />
                  </Section>
                </Slot>

                <Slot gap="4" show={filtering && found.count === 0}>
                  <EmptyState
                    title={`No charge in this period matches “${query.trim()}”`}
                    action={(
                      <button onClick={() => setQuery('')} className={buttonClass('tonal', 'md')}>
                        Clear search
                      </button>
                    )}
                  >
                    Try a shorter piece of the name, or the amount on its own. Search covers the charge, the matched receipt, and the cardholder.
                  </EmptyState>
                </Slot>

                <Slot gap="4" show={!filtering && fullyDone}>
                  <EmptyState variant="area">This period is fully reconciled.</EmptyState>
                </Slot>
              </>
            ) : null}
            </FadeIn>
          </main>
      </div>

      <ReceiptPeek pid={peekPid} onClose={() => setPeekPid(null)}
        requisitions={peekPid ? board?.transactions.find((x) => x.match?.receipt_public_id === peekPid)?.match?.requisitions : undefined} />
    </PageLayout>
  );
}

// The rail's identity card: which account, which period, and the files whose
// charges it holds. The dates come from the account's closing day; an open
// period's close date can be moved here when the real statement drifted.
function PeriodIdentity({ board, onCloseChanged }: { board: ReconBoard; onCloseChanged: () => Promise<void> | void }) {
  const { account, period, uploads, tally } = board;
  const outside = board.outside_count || 0;
  const [editingClose, setEditingClose] = useState(false);
  // A new period on the same board (route change) must not inherit an open editor.
  useEffect(() => { setEditingClose(false); }, [period.id]);
  return (
    <div className="space-y-1.5">
      <div className="text-xs font-medium text-on-surface-variant truncate" title={account.label}>{account.label}</div>
      <div className="flex flex-wrap items-center gap-2 min-w-0">
        <span className="text-sm font-medium text-on-surface truncate">{period.display_label}</span>
        {period.is_current ? <CurrentChip /> : <PeriodChip status={period.status} />}
        {period.status === 'open' && !editingClose && (
          <Tooltip label="Edit close date" note="The statement closed on a different day? Set it here; the next period starts the day after.">
            <IconButton size="sm" label="Edit close date" onClick={() => setEditingClose(true)}>
              <IconPencil />
            </IconButton>
          </Tooltip>
        )}
      </div>
      <Slot gap="1.5" show={editingClose}>
        <CloseDateEditor period={period}
          onCancel={() => setEditingClose(false)}
          onSaved={async () => { setEditingClose(false); await onCloseChanged(); }} />
      </Slot>
      <div className="text-[11px] text-on-surface-variant">
        {tally.total} charge{tally.total === 1 ? '' : 's'}{period.is_current ? ' so far' : ''}
      </div>
      <Slot gap="1.5" show={outside > 0}>
        <p className="text-[11px] text-amber-300/90">
          {outside} charge{outside === 1 ? '' : 's'} on this account {outside === 1 ? 'falls' : 'fall'} outside every period
        </p>
      </Slot>
      {uploads.length > 0 && (
        <ul className="pt-1.5 space-y-1 border-t border-outline-variant" aria-label="Statements in this period">
          {uploads.map((u) => (
            <li key={u.id} className="min-w-0 text-[11px] leading-snug">
              <div className="truncate text-on-surface" title={u.source_filename || u.label || ''}>{u.source_filename || u.label || 'Statement'}</div>
              <div className="truncate text-on-surface-variant">
                {formatTimestamp(u.created_at)}{u.imported_by_name ? ` by ${u.imported_by_name}` : ''}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// The close date of an open period. A real statement drifts a day or two from the
// account's closing day; the server re-cuts the next period from the day after.
function CloseDateEditor({ period, onSaved, onCancel }: {
  period: BillingPeriod; onSaved: () => Promise<void> | void; onCancel: () => void;
}) {
  const toast = useToast();
  const initial = period.end_date ? String(period.end_date).slice(0, 10) : '';
  const dateId = useFormId('close-date');
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!draft) { setError('Pick the day the statement closed'); return; }
    if (draft === initial) { onCancel(); return; }
    setSaving(true);
    setError(null);
    try {
      await apiFetchAuthed(`/reconciliation/periods/${period.id}`, { method: 'PATCH', body: { end_date: draft }, silent: true });
      toast.success('Close date updated');
      await onSaved();
    } catch (e: any) {
      setError(serverMessage(e, 'Could not change the close date'));
      setSaving(false);
    }
  };

  return (
    <form className="space-y-1.5 p-0.5" onSubmit={(e) => { e.preventDefault(); save(); }}
      // Escape cancels the edit only: stopped here so the board's window listener
      // does not also drop the keyboard cursor or close a peek on the same press.
      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onCancel(); } }}>
      <FieldRow label="Closed on" htmlFor={dateId}>
        <Field id={dateId} autoFocus type="date" value={draft} onChange={(e) => setDraft(e.target.value)} disabled={saving} />
      </FieldRow>
      {error && <Alert variant="text" className="text-xs">{error}</Alert>}
      <div className="flex items-center gap-3">
        <Tooltip label="Save the close date. The next period starts the day after">
          <button type="submit" disabled={saving}
            className={buttonClass('filled', 'sm')}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </Tooltip>
        <button type="button" onClick={onCancel} disabled={saving}
          className={buttonClass('quiet', 'sm')}>Cancel</button>
      </div>
    </form>
  );
}

// How one statement file was handled at import: what was left out, repeats kept,
// credits, rows already on file from an earlier statement, cards not on the roster.
function ImportCheckBody({ summary: isum, name }: { summary: ImportSummary; name: string | null }) {
  const already = isum.already_on_file || 0;
  const refused = isum.into_finalized;
  return (
    <div className="space-y-2.5 px-4 py-3 text-sm text-on-surface">
      {name && <div className="font-medium text-on-surface truncate" title={name}>{name}</div>}
      {name && (
        <p className="text-on-surface-variant">
          {isum.kept} of {isum.file_rows} file rows read, {formatEventDate(isum.period_start)} to {formatEventDate(isum.period_end)}.
        </p>
      )}
      {already > 0 && (
        <p>
          {already} charge{already === 1 ? ' was' : 's were'} already on file from an earlier statement, so {already === 1 ? 'it was' : 'they were'} not added twice.
        </p>
      )}
      {refused && refused.count > 0 && (
        <div>
          <div className="font-medium text-amber-300/90">
            Not taken (finalized period)
            <span className="ml-2 font-normal text-on-surface-variant">
              {refused.count} charge{refused.count === 1 ? '' : 's'} in {refused.periods.map((p) => `${p.display_label} (${p.count})`).join(', ')}
            </span>
          </div>
          <ul className="mt-1 ml-4 list-disc space-y-0.5 text-on-surface-variant">
            {refused.detail.map((x, i) => (
              <li key={i} className="truncate">{formatEventDate(x.txn_date)} · {x.description || '(no description)'} · <span className="font-mono tabular-nums">{txnMoney(x.amount)}</span></li>
            ))}
            {refused.count > refused.detail.length && <li className="text-on-surface-variant">and {refused.count - refused.detail.length} more</li>}
          </ul>
          <p className="mt-1 text-amber-300/90">Reopen that period and import the file again to take them.</p>
        </div>
      )}
      {isum.excluded > 0 && (
        <div>
          <div className="font-medium text-on-surface">
            {isum.excluded} row{isum.excluded === 1 ? '' : 's'} left out
            <span className="ml-2 font-normal text-on-surface-variant">card payments and fees, not purchases</span>
          </div>
          <ul className="mt-1 ml-4 list-disc space-y-0.5 text-on-surface-variant">
            {isum.excluded_detail.map((x, i) => (
              <li key={i} className="truncate">{x.description}{x.type ? ` (${x.type})` : ''}{x.amount != null && <> · <span className="font-mono tabular-nums">${Math.abs(Number(x.amount)).toFixed(2)}</span></>}</li>
            ))}
            {isum.excluded > isum.excluded_detail.length && <li className="text-on-surface-variant">and {isum.excluded - isum.excluded_detail.length} more</li>}
          </ul>
        </div>
      )}
      {isum.repeated_rows > 0 && (
        <div>
          <div className="font-medium text-on-surface">
            {isum.repeated_rows} extra cop{isum.repeated_rows === 1 ? 'y' : 'ies'} across {isum.repeated_groups} charge{isum.repeated_groups === 1 ? '' : 's'} kept
            <span className="ml-2 font-normal text-on-surface-variant">the same thing bought more than once; every copy is its own charge in the piles</span>
          </div>
          <ul className="mt-1 ml-4 list-disc space-y-0.5 text-on-surface-variant">
            {isum.repeated_detail.map((g, i) => (
              <li key={i} className="truncate">{formatEventDate(g.txn_date)} · {g.description || '(no description)'} · <span className="font-mono tabular-nums">${Math.abs(Number(g.amount)).toFixed(2)}</span> × {g.count}</li>
            ))}
            {isum.repeated_groups > isum.repeated_detail.length && <li className="text-on-surface-variant">and {isum.repeated_groups - isum.repeated_detail.length} more charges with repeats</li>}
          </ul>
        </div>
      )}
      {isum.credits > 0 && (
        <p>{isum.credits} credit{isum.credits === 1 ? '' : 's'} auto-marked as refunds. They are in the Refunds pile below.</p>
      )}
      {isum.unknown_cards.length > 0 && (
        <p className="text-amber-300/90">
          These card numbers are not on the roster yet: <span className="font-mono tabular-nums">{isum.unknown_cards.join(', ')}</span>. Add them on Manage cards, then use Re-run matching.
        </p>
      )}
    </div>
  );
}

// ── cardholder groups within a section ─────────────────────────────────────────
function GroupedBody({
  tier, txns, tint, openGroups, onToggle, forceOpen, focusedId, renderAction, renderExpansion, renderCategory, renderNote,
  nudgeRecent, picked, onTogglePick, pickDisabled, paired, onPeek, onConfirmGroup, busy,
}: {
  tier: Tier; txns: ReconTxn[]; tint: string;
  openGroups: Record<string, boolean>; onToggle: (k: string) => void;
  // Cardholder groups are collapsed by default, so a search that only narrowed the
  // arrays would surface nothing. Derived rather than written into openGroups, so
  // her own collapse state survives the search untouched and needs no restoring.
  forceOpen?: boolean;
  focusedId: number | null;
  renderAction: (txn: ReconTxn) => React.ReactNode;
  renderExpansion?: (txn: ReconTxn) => React.ReactNode;
  // Matched piles only — Dawn's inline expense-category control, rendered inside the pair row.
  renderCategory?: (txn: ReconTxn) => React.ReactNode;
  // A sentence under an unpaired charge (the subscription question in Needs your eyes).
  renderNote?: (txn: ReconTxn) => string | null;
  // No-receipt pile only — check a cardholder, then Nudge selected sends the set.
  nudgeRecent?: Record<string, string>;
  picked?: ReadonlySet<string>;
  onTogglePick?: (userId: string) => void;
  pickDisabled?: boolean;
  // Matched piles (confident/review): render side-by-side statement↔receipt cells.
  paired?: boolean;
  onPeek?: (pid: string) => void;
  // Confident pile only — per-cardholder-section confirm (replaces the global "Confirm all").
  onConfirmGroup?: (cardholder: string, txnIds: number[]) => void;
  busy?: boolean;
}) {
  return (
    <>
      {groupByCard(txns).map(([cardholder, rows]) => {
        const key = `${tier}::${cardholder}`;
        const cardOpen = forceOpen || !!openGroups[key];
        const ownerId = tier === 'noreceipt' ? rows.find((r) => r.card_owner_user_id)?.card_owner_user_id : null;
        return (
          <div key={key}>
            <div className="np-state w-full px-4 py-2 bg-surface-container-high text-xs font-semibold text-on-surface flex items-center gap-2">
              {ownerId && onTogglePick && (
                <Checkbox size="sm" className="shrink-0 justify-center w-7 h-7 -my-1"
                  checked={!!picked?.has(ownerId)} disabled={pickDisabled}
                  aria-label={`Include ${cardholder}`}
                  onChange={() => onTogglePick(ownerId)} />
              )}
              <button type="button" onClick={() => onToggle(key)} aria-expanded={cardOpen}
                className={`flex items-center gap-2 min-w-0 flex-1 rounded text-left ${FOCUS}`}>
                <Chevron open={cardOpen} />
                <span className="truncate">{cardholder}</span>
              </button>
              <span className="text-on-surface-variant shrink-0">{rows.length} charge{rows.length === 1 ? '' : 's'}</span>
              {onConfirmGroup && (
                <button type="button" onClick={() => onConfirmGroup(cardholder, rows.map((r) => r.id))} disabled={busy}
                  className={`${buttonClass('tonal', 'sm')} shrink-0`}>
                  Confirm {rows.length}
                </button>
              )}
              <Appear show={!!ownerId && !!nudgeRecent?.[ownerId]} className="font-normal text-on-surface-variant whitespace-nowrap">
                Nudged {ownerId && nudgeRecent?.[ownerId] ? nudgeCaption(nudgeRecent[ownerId]) : ''}
              </Appear>
            </div>
            <Reveal show={cardOpen}>
            {rows.map((txn) => (
              <React.Fragment key={txn.id}>
                {paired && txn.match && onPeek ? (
                  <MatchPairRow txn={txn} tint={tint} focused={txn.id === focusedId} onPeek={onPeek} action={renderAction(txn)} categoryControl={renderCategory?.(txn)} />
                ) : (
                  <ChargeRow txn={txn} tint={tint} focused={txn.id === focusedId} categoryControl={renderCategory?.(txn)} note={renderNote?.(txn)}>{renderAction(txn)}</ChargeRow>
                )}
                {renderExpansion?.(txn)}
              </React.Fragment>
            ))}
            </Reveal>
          </div>
        );
      })}
    </>
  );
}

// ── section shell (matches ChargerInconsistencies: rotating SVG chevron) ───────
function Chevron({ open }: { open: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      className={`flex-shrink-0 text-on-surface-variant transition-transform motion-reduce:transition-none ${open ? 'rotate-90' : ''}`}>
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

const BADGE: Record<string, 'good' | 'warn' | 'neutral'> = {
  green: 'good',
  amber: 'warn',
  gray: 'neutral',
};

// Container tone. A pile sits one step up from the page; 'attention' adds an amber
// ring (outside, so the group headers inside cannot paint over it) and 'muted' lets a
// resolved pile recede a step.
const ATTENTION_RING = 'ring-1 ring-amber-500/40';

function Section({
  title, count, total, badge, hint, tone = 'neutral', id, open, onToggle, actions, children,
}: {
  // `count` is what this pile is showing; `total` is what it holds. They differ
  // only while a search narrows the pile, and then the badge says so rather than
  // quietly under-reporting the statement.
  title: string; count: number; total?: number; badge: string; hint: string; tone?: string; id?: string;
  open: boolean; onToggle: () => void; actions?: React.ReactNode; children: React.ReactNode;
}) {
  const label = total != null && total !== count ? `${count} of ${total}` : `${count}`;
  return (
    <Panel id={id} label={title} padding="none" on={tone === 'muted' ? 'ground' : 'panel'}
      className={`scroll-mt-6 overflow-hidden ${tone === 'attention' ? ATTENTION_RING : ''}`}>
      <div className="np-state flex items-center justify-between gap-2 px-4 py-3">
        <button type="button" onClick={onToggle} aria-expanded={open}
          className="flex items-center gap-2 min-w-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded">
          <Chevron open={open} />
          <span className="text-sm font-semibold text-on-surface">{title}</span>
          <Tag tone={BADGE[badge]} className="flex-shrink-0 tabular-nums">{label}</Tag>
          <span className="text-xs text-on-surface-variant truncate">{hint}</span>
        </button>
        {actions && <div className="flex-shrink-0">{actions}</div>}
      </div>
      <Reveal show={open}><div className="border-t border-outline-variant divide-y divide-outline-variant">{children}</div></Reveal>
    </Panel>
  );
}

// ── row + helpers ──────────────────────────────────────────────────────────────
const TINT: Record<string, string> = {
  confident: 'np-state',
  review: 'np-state',
  none: 'np-state',
  done: 'np-state opacity-70 hover:opacity-100',
};

// The cardholder's answer counts only while the charge is still unmatched: once a
// receipt pairs with it, the answer is history.
const hasAnswer = (t: ReconTxn) => !!t.cardholder_response && t.match_status === 'unmatched';
const isFlagged = (t: ReconTxn) => hasAnswer(t) && t.cardholder_response === 'suspicious';

function FlaggedCount({ n }: { n: number }) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold tabular-nums text-red-300">
      <img src={susStamp} alt="" draggable={false} className="sus-stamp h-4 w-auto select-none" />
      {n} flagged
    </span>
  );
}

// What the cardholder said about an unmatched charge. The stamp carries the
// name for a reader; the pill's word carries the state beside its colour.
function CardholderAnswer({ txn }: { txn: ReconTxn }) {
  if (txn.cardholder_response === 'suspicious') return (
    <span className="shrink-0 inline-flex items-center gap-1">
      <span role="img" aria-label="Flagged suspicious by the cardholder" className="inline-flex">
        <img src={susStamp} alt="" draggable={false} className="sus-stamp h-4 w-auto select-none" />
      </span>
      <Tag size="sm" tone="bad">Flagged</Tag>
    </span>
  );
  if (txn.cardholder_response === 'no_receipt') return (
    <Tag size="sm" className="shrink-0">Doesn't have it</Tag>
  );
  return null;
}

function ChargeRow({ txn, tint, focused, children, categoryControl, note }: { txn: ReconTxn; tint: string; focused?: boolean; children: React.ReactNode; categoryControl?: React.ReactNode; note?: string | null }) {
  const answered = hasAnswer(txn);
  const answerNote = answered ? txn.cardholder_response_note : null;
  const secondLine = !!note || !!answerNote;
  return (
    <div data-rowid={txn.id}
      className={`grid grid-cols-[6.5rem_7rem_minmax(0,1fr)_auto] items-center gap-4 px-4 py-2.5 transition ${TINT[tint]} ${focused ? 'relative z-10 ring-2 ring-primary/60' : ''}`}>
      <span className="text-xs text-on-surface-variant tabular-nums" title={txn.txn_date}>{formatEventDate(txn.txn_date, 'MMM D')}</span>
      <span className="text-sm font-mono tabular-nums text-on-surface text-right">{txnMoney(txn.amount)}</span>
      <span className="min-w-0 flex items-center gap-1.5 text-sm text-on-surface">
        {isCredit(txn.amount) && (
          <Tag size="sm" className="shrink-0">credit</Tag>
        )}
        <span className="min-w-0 truncate">{txn.description || '—'}</span>
        {answered && <CardholderAnswer txn={txn} />}
      </span>
      <div className={`justify-self-end flex items-center gap-3 ${secondLine ? 'row-span-2' : ''}`}>{categoryControl}{children}</div>
      {secondLine && (
        // One grid item under date, amount and description, so the action cell's
        // row-span-2 still covers it when both lines are present.
        <div className="col-span-3 -mt-2 min-w-0 space-y-0.5 text-xs">
          {note && <p className="truncate text-amber-200/90" title={note}>{note}</p>}
          {answerNote && <p className="break-words text-on-surface-variant">Cardholder's note: {answerNote}</p>}
        </div>
      )}
    </div>
  );
}

// A matched row (Confident / Needs-eyes) rendered as side-by-side statement↔receipt
// cells instead of the flat charge line. Same data-rowid / focus-ring / tint shell
// as ChargeRow, so the keyboard cursor and hover treatment carry over unchanged.
function MatchPairRow({ txn, tint, focused, onPeek, action, categoryControl }: {
  txn: ReconTxn; tint: string; focused?: boolean;
  onPeek: (pid: string) => void; action: React.ReactNode; categoryControl?: React.ReactNode;
}) {
  return (
    <div data-rowid={txn.id}
      className={`px-4 py-2.5 transition ${TINT[tint]} ${focused ? 'relative z-10 ring-2 ring-primary/60' : ''}`}>
      <MatchPairGrid txn={txn} onPeek={onPeek} action={action} categoryControl={categoryControl} />
    </div>
  );
}

// Dawn's inline override for one taggable dimension of a matched row (expense
// category or purchase class). Plain when the value is human-set; an amber "Not sure"
// pill + ring when the user deferred it (blocks export until resolved); a soft "Auto"
// pill when it was auto-picked from the card at folder import (flows — her leaving it
// is acceptance — she can still change it). Reused for both dimensions, stacked, so
// the row never widens.
function InlineOverride({ prefix, ariaLabel, options, value, deferred, autoPicked, busy, onSet, setPlaceholder, deferredPlaceholder }: {
  prefix: string;
  ariaLabel: string;
  options: { id: number; label: string }[];
  value: number | null;
  deferred: boolean;
  autoPicked?: boolean;
  busy: boolean;
  onSet: (id: number) => void;
  setPlaceholder: string;
  deferredPlaceholder: string;
}) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="shrink-0 w-20 text-[11px] text-on-surface-variant">{prefix}</span>
      {deferred ? (
        <Tag size="sm" tone="warn" className="shrink-0">Not sure</Tag>
      ) : autoPicked ? (
        <Tag size="sm" tone="info" className="shrink-0">Auto</Tag>
      ) : null}
      <div className={`min-w-0 max-w-[13rem] rounded-control ${deferred ? 'ring-1 ring-amber-500/50' : ''}`}>
        <Select
          label={ariaLabel}
          size="sm"
          width="fit"
          value={value != null ? String(value) : null}
          placeholder={deferred ? deferredPlaceholder : setPlaceholder}
          disabled={busy}
          options={options.map((o) => ({ key: String(o.id), label: o.label }))}
          onChange={(k) => { const v = Number(k); if (v) onSet(v); }}
        />
      </div>
    </div>
  );
}

// Inline result panel for the QBO sync/dry-run action. Same shape for a dry
// run (unconfigured QBO — preview only) and a real sync, just different
// framing: the dry run always lists purchases + the "no data left" caption;
// a real sync leans on the toast for the happy path and only earns this panel
// back to surface partial-failure detail (skipped rows).
function QboResultPanel({ result, onDismiss }: { result: QboSyncResult; onDismiss: () => void }) {
  return (
    <Panel label="QBO sync result" on="panel" padding="sm" className="gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-on-surface">
          {result.dryRun
            ? `Dry run preview · ${result.count ?? 0} ${result.count === 1 ? 'purchase' : 'purchases'}`
            : `Synced ${result.synced ?? 0}${result.alreadySynced ? ` (+${result.alreadySynced} already synced)` : ''}`}
        </span>
        <button onClick={onDismiss} className={buttonClass('quiet', 'sm')}>dismiss</button>
      </div>
      {result.purchases && result.purchases.length > 0 && (
        <div className="divide-y divide-outline-variant max-h-64 overflow-y-auto">
          {result.purchases.slice(0, 10).map((p, i) => (
            <div key={i} className="grid grid-cols-[3.5rem_3.5rem_minmax(0,1fr)_minmax(0,1fr)] gap-2 py-1.5 text-[11px] text-on-surface">
              <span className="text-on-surface-variant tabular-nums">{p.date ? formatEventDate(p.date) : '—'}</span>
              <span className="font-mono tabular-nums text-on-surface text-right">{money(p.amount)}</span>
              <span className="min-w-0 truncate">{p.account_name || '—'}</span>
              <span className="min-w-0 truncate text-on-surface-variant">{p.memo || ''}</span>
            </div>
          ))}
        </div>
      )}
      {result.skipped && result.skipped.length > 0 && (
        <Alert tone="warning" variant="text" className="text-xs">
          {result.skipped.length} skipped: {result.skipped.map((s) => s.reason).join(', ')}
        </Alert>
      )}
      {result.errors && result.errors.length > 0 && (
        <Alert variant="text" className="text-xs">
          {result.errors.length} failed (safe to re-run, synced rows are skipped): {result.errors.map((e) => e.error).join('; ')}
        </Alert>
      )}
      {result.dryRun && (
        <p className="text-[10px] text-on-surface-variant">
          Dry run. No data left {instance.displayName}. Configure QBO credentials to go live (docs/operations/qbo-setup.md).
        </p>
      )}
    </Panel>
  );
}
